// 🧠 Core React & Form Libraries
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm, Controller, Form } from "react-hook-form";
import { FormStepContext } from "./formContext";

// 📅 Booking Calendar
// import { Calendar, CalendarReserved } from "@demark-pro/react-booking-calendar";
import "@demark-pro/react-booking-calendar/dist/react-booking-calendar.css";
import "./calendar-overrides.css";

// 🌐 Networking & Data Fetching
import axios from "axios";
import { useQuery, useQueries } from "@tanstack/react-query";

// 🔤 String Formatting
import { titleCase } from "title-case";

import ClassSelector from "./ClassSelector";
import GuestDetails from "./GuestDetails";
import CalendarBlock from "./CalendarBlock";
import PricingSummary from "./PricingSummary";
import Footer from "./Footer";
import FormStep1 from "./FormStep1";
import FormStep2 from "./FormStep2";
import FormStep3 from "./FormStep3";
import CouponCheckout from "./CouponCheckout";
import ProgressIndicator from "./ProgressIndicator";
import LeftPane from "./LeftPane";
import MobileBookingLayout from "./MobileBookingLayout";
import LeftPaneMobile from "./LeftPaneMobile";

import { CLASS_CATALOG, CLASS_BY_NAME, SUITE_BY_NAME } from "./classCatalog";
import pricingData from "./utils/pricing.json";

import "./App_settings.css";
// import { parse } from "path";
// import { suite } from "node:test";

//testing stuff
export default function App() {
  // console.log("CLASS_BY_NAME keys:", Object.keys(CLASS_BY_NAME));
  // console.log("All Classes entry:", CLASS_BY_NAME["All Classes"]);
  // ✅ Detect if this is a prescheduled page
  const isPrescheduledPage = window.location.href.includes("prescheduled-classes") || window.location.href.includes("-fixed");
  // console.log("Page type detection:", {
  //   href: window.location.href,
  //   isPrescheduledPage,
  // });

  // Batch queries that drive calendar + guest details
  const results = useQueries({
    queries: [
      {
        queryKey: ["dates"],
        queryFn: async () => {
          try {
            const response = await axios.get("/dates");
            // console.log("✅ /dates response:", response.data);
            return response.data;
          } catch (error) {
            console.error("❌ /dates error:", error);
            throw error;
          }
        },
        refetchInterval: 5 * 60 * 1000,
      },
      {
        queryKey: ["bookings"],
        queryFn: async () => {
          try {
            const response = await axios.get("/booking_records");
            // console.log("✅ /booking_records response:", response.data);
            return response.data;
          } catch (error) {
            console.error("❌ /booking_records error:", error);
            throw error;
          }
        },
        refetchInterval: 5 * 60 * 1000,
      },
      {
        queryKey: ["productCapacityMap"],
        queryFn: async () => {
          try {
            const response = await axios.get("/product-capacity-map");
            console.log("✅ /product-capacity-map response:", response.data);
            return response.data;
          } catch (error) {
            console.error("❌ /product-capacity-map error:", error);
            throw error;
          }
        },
        refetchInterval: 5 * 60 * 1000,
      },
      {
        queryKey: ["booking_details"],
        queryFn: async () => {
          // console.log("➡️ Fetching /booking_details …");
          try {
            const response = await axios.get("/booking_details");
            // console.log("✅ /booking_details response:", response.data);
            return response.data;
          } catch (error) {
            console.error("❌ /booking_details error:", error);
            throw error;
          }
        },
        refetchInterval: 5 * 60 * 1000,
      },
    ],
  });

  // Destructure in the same order
  const [dateQuery = {}, bookingsQuery = {}, productCapacityMapQuery = {}, bookingDetailsQuery = {}] = results;

  // useEffect(() => {
  //   console.log("🔎 Query states:", {
  //     dates: dateQuery.status,
  //     bookings: bookingsQuery.status,
  //     productCapacity: productCapacityMapQuery.status,
  //     bookingDetails: bookingDetailsQuery.status,
  //   });
  // }, [dateQuery.status, bookingsQuery.status, productCapacityMapQuery.status, bookingDetailsQuery.status]);

  // Unified loading flag
  // const calendarAndGuestLoading = dateQuery.isLoading || bookingsQuery.isLoading || productCapacityMapQuery.isLoading;

  // const dateQuery = useQuery({
  //   queryKey: ["date"],
  //   queryFn: async () => {
  //     // console.log("➡️ Fetching /dates …");
  //     try {
  //       const response = await axios.get("/dates");
  //       // console.log("✅ /dates response:", response.data);
  //       return response.data;
  //     } catch (error) {
  //       console.error("❌ /dates error:", error);
  //       throw error;
  //     }
  //   },
  //   refetchInterval: 5 * 60 * 1000,
  // });

  // const bookingsQuery = useQuery({
  //   queryKey: ["bookings"],
  //   queryFn: async () => {
  //     // console.log("➡️ Fetching /booking_records …");
  //     try {
  //       const response = await axios.get("/booking_records");
  //       // console.log("✅ /booking_records response:", response.data);
  //       return response.data;
  //     } catch (error) {
  //       console.error("❌ /booking_records error:", error);
  //       throw error;
  //     }
  //   },
  //   refetchInterval: 5 * 60 * 1000,
  // });

  // const bookingDetailsQuery = useQuery({
  //   queryKey: ["booking_details"],
  //   queryFn: async () => {
  //     // console.log("➡️ Fetching /booking_details …");
  //     try {
  //       const response = await axios.get("/booking_details");
  //       // console.log("✅ /booking_details response:", response.data);
  //       return response.data;
  //     } catch (error) {
  //       console.error("❌ /booking_details error:", error);
  //       throw error;
  //     }
  //   },
  //   refetchInterval: 5 * 60 * 1000,
  // });

  // const productCapacityMapQuery = useQuery({
  //   queryKey: ["productCapacityMap"],
  //   queryFn: async () => {
  //     // console.log("➡️ Fetching /product-capacity-map …");
  //     try {
  //       const response = await axios.get("/product-capacity-map");
  //       // console.log("✅ /product-capacity-map response:", response.data);
  //       return response.data; // [{ productId, productName, groupId, groupName, max, min }]
  //     } catch (error) {
  //       console.error("❌ /product-capacity-map error:", error);
  //       throw error;
  //     }
  //   },
  //   staleTime: Infinity,
  // });

  // ✅ Parse Bookingmood date-only strings as LOCAL calendar dates (no timezone shift)
  function parseDateOnlyToLocal(s: string): Date {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d); // local midnight
  }

  // ✅ Format a Date as YYYY-MM-DD using local calendar fields
  function toLocalYMD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  useEffect(() => {
    if (!productCapacityMapQuery.data) return;

    const classImages = CLASS_CATALOG.map((c) => c.imageUrl).filter(Boolean);
    const suiteImages = (productCapacityMapQuery.data ?? []).flatMap((p) => p.images || []).filter(Boolean);

    const allImages = Array.from(new Set([...classImages, ...suiteImages]));

    allImages.forEach((src) => {
      const img = new Image();
      img.src = src;

      // ⭐ Force decode so the browser caches the decoded bitmap
      img.decode?.().catch(() => {});
    });
  }, [productCapacityMapQuery.data]);

  useEffect(() => {
    const urls: string[] = [];

    // 1. Class modal pages
    CLASS_CATALOG.forEach((item) => {
      if (!item.sections) return;

      Object.values(item.sections).forEach((val) => {
        if (typeof val === "string" && val.startsWith("http")) {
          urls.push(val);
        }
      });
    });

    // 2. Suite modal pages (from SUITE_BY_NAME)
    Object.values(SUITE_BY_NAME).forEach((suite) => {
      if (!suite.sections) return;

      Object.values(suite.sections).forEach((val) => {
        if (typeof val === "string" && val.startsWith("http")) {
          urls.push(val);
        }
      });
    });

    const uniqueUrls = Array.from(new Set(urls));

    uniqueUrls.forEach((url) => {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.href = url;
      link.as = "document";
      document.head.appendChild(link);
    });

    // console.log("📄 Prefetched modal pages:", uniqueUrls.length);
  }, []);

  // const classOptions = [
  //   // 3-Day Classes
  //   ["Boot Camp", "La Patisserie", "Effortless Entertaining", "Guide to Sauces", "French Classics"],

  //   // 5-Day Classes
  //   [
  //     "Culinary Academy",
  //     "Foundational French",
  //     "The Pastry Shop",
  //     "Around the World",
  //     "The Improv Sessions",
  //     "Comprehensive Sauces",
  //     "Superfoods",
  //     "How To...",
  //     "Elegant Entertaining",
  //     "The 101's",
  //   ],
  // ];
  const catalogClassesForLookup = useMemo(() => {
    return CLASS_CATALOG.filter((c) => {
      const item = c as any;
      return !item.roomType && item.name !== "All Classes";
    });
  }, []);

  const catalogClassesForDropdowns = useMemo(() => {
    return CLASS_CATALOG.filter((c) => {
      const item = c as any;
      return !item.roomType && item.name !== "All Classes" && item.listInDropdown !== false;
    });
  }, []);

  const classOptions = useMemo(() => {
    const sortByName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" });

    const threeDay = catalogClassesForDropdowns
      .filter((c) => c.dayType === "3-day")
      .map((c) => c.name)
      .sort(sortByName);

    const fiveDay = catalogClassesForDropdowns
      .filter((c) => c.dayType === "5-day")
      .map((c) => c.name)
      .sort(sortByName);

    return [threeDay, fiveDay];
  }, [catalogClassesForDropdowns]);

  const allCatalogClassNames = useMemo(() => {
    return catalogClassesForLookup.map((c) => c.name);
  }, [catalogClassesForLookup]);

  const allClassNames = [...classOptions[0], ...classOptions[1]];

  const classDurationMap = Object.fromEntries([
    ...catalogClassesForLookup.filter((c) => c.dayType === "3-day").map((c) => [c.name, "0"]),

    ...catalogClassesForLookup.filter((c) => c.dayType === "5-day").map((c) => [c.name, "1"]),
  ]);

  const normalizeClassKey = (s: string = "") =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]/gi, "")
      .trim();

  const fuzzyMatchClassName = (rawName: string) => {
    const cleaned = normalizeClassKey(rawName);

    const match = catalogClassesForLookup.find((c) => {
      const item = c as any;

      const candidates = [item.name, item.displayName, item.slug, ...(item.aliases || [])].filter(Boolean);

      return candidates.some((candidate) => {
        const normalized = normalizeClassKey(candidate);
        return cleaned.includes(normalized) || normalized.includes(cleaned);
      });
    });

    return match?.name || rawName;
  };

  const parsedUrlInfo = useMemo(() => {
    const href = window.parent !== window ? window.parent.location.href : window.location.href;
    const urlTail = href.split("/p/")[1] || "";
    // const isFixedPage = href.includes("-fixed") || href.includes("prescheduled-classes");

    // ✅ Detect room reservation mode
    const isRoomMode = href.includes("/rooms/");
    if (isRoomMode) {
      const slug = href.split("/rooms/")[1]?.split("/")[0] || "";
      const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/gi, "");

      // Fuzzy match slug against productCapacityMap data
      const roomEntry = productCapacityMapQuery?.data?.find(({ productName }) => {
        const normalized = normalize(productName || "");
        const cleanedSlug = normalize(slug);
        return cleanedSlug.includes(normalized) || normalized.includes(cleanedSlug);
      });

      return {
        isRoomMode: true,
        isGlobalSearch: false,
        className: roomEntry?.productName || titleCase(slug.replace(/-/g, " ")),
        classLen: "", // not used in room mode
        urlTail: slug,
        href,
        productId: roomEntry?.productId || null,
      };
    }

    // ✅ Prescheduled "All Classes" fixed page
    if (href.includes("prescheduled-classes")) {
      return { isRoomMode: false, isGlobalSearch: false, className: "All Classes", classLen: "", urlTail, href };
    }

    // ✅ Specific fixed class page (strip "-fixed" and normalize)
    if (urlTail.endsWith("-fixed")) {
      const rawFixed = urlTail.replace("-fixed", "").trim();

      const catalogMatch = catalogClassesForLookup.find((c) => c.slug === rawFixed);

      const className = catalogMatch?.name || fuzzyMatchClassName(rawFixed.replace(/-/g, " "));
      const resolved = !!CLASS_BY_NAME[className];

      if (resolved) {
        const classLen = classDurationMap[className] || "0";
        console.log("Resolved fixed class:", className, classLen);
        return { isRoomMode: false, isGlobalSearch: false, className, classLen, urlTail, href };
      } else {
        console.log("Unresolved fixed class, falling back to All Classes");
        // 👇 fallback: treat as prescheduled "All Classes"
        return { isRoomMode: false, isGlobalSearch: false, className: "All Classes", classLen: "", urlTail, href };
      }
    }

    // ✅ Global "all_classes" page
    if (urlTail === "all_classes") {
      return { isRoomMode: false, isGlobalSearch: true, className: "All Classes", classLen: "", urlTail, href };
    }

    // ✅ Normal class page parsing
    const raw = urlTail.replace(/-/g, " ");
    const parts = raw.split(/([0-9])/);
    const rawName = parts[0]
      ?.trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/gi, "");
    const lenNum = parseInt(parts[1], 10);
    const classLen = lenNum === 3 ? "0" : lenNum === 5 ? "1" : "0";

    const classLookupMap = Object.fromEntries(
      [...classOptions[0], ...classOptions[1]].map((name) => {
        const normalized = name.toLowerCase().replace(/[^a-z0-9]/gi, "");
        return [normalized, name];
      }),
    );

    const className = classLookupMap[rawName] || titleCase(parts[0]?.trim() || "Unknown");

    return { isRoomMode: false, isGlobalSearch: false, className, classLen, urlTail, href };
  }, [productCapacityMapQuery.data]);

  // useEffect(() => {
  //   console.log("🔍 parsedUrlInfo computed:", parsedUrlInfo);
  //   console.log("🔍 productCapacityMapQuery.data at parse time:", productCapacityMapQuery.data);
  // }, [parsedUrlInfo]);

  // ✅ Mode flags
  const isFixedMode = isPrescheduledPage || parsedUrlInfo.isGlobalSearch;
  const isRoomMode = parsedUrlInfo.isRoomMode;
  const isSpecificFixedClassPage =
    parsedUrlInfo.urlTail?.endsWith("-fixed") && parsedUrlInfo.className && parsedUrlInfo.className !== "All Classes";

  // 📝 Initialize React Hook Form
  const {
    register, // Registers input fields for tracking and validation
    formState: { errors, isValid }, // Tracks validation errors and overall form validity
    watch, // Observes field changes in real time
    resetField, // Resets individual fields to default or new values
    control, // Used for controlled components (e.g., calendar, phone input)
    setValue, // Programmatically sets field values
    getValues, // Retrieves current field values
    trigger, // Manually triggers validation for specific fields
  } = useForm({
    defaultValues: {
      NumberofGuests: "1",
      Occupancy: "1 Single Room",
      Experience: isFixedMode ? parsedUrlInfo.classLen : "",
      ExperienceTitle: isRoomMode ? "" : isFixedMode ? parsedUrlInfo.className : parsedUrlInfo.className || "Boot Camp",

      dates: [],
      PostalCode: "",
      Apartment: "",
      Address: "",
      City: "",
      State: "",
      Country: "",
      PrivateExperience: false,
      couponCode: "",
    },
    mode: "all", // Validates on change, blur, and submit
  });
  /* const[csrfToken, setCsrfToken] = useState('') */

  useEffect(() => {
    if (!isRoomMode) return;
    if (!productCapacityMapQuery.data) return; // ⬅️ wait for product data
    if (!parsedUrlInfo.className) return;

    setValue("ExperienceTitle", parsedUrlInfo.className, { shouldValidate: true });
  }, [isRoomMode, productCapacityMapQuery.data, parsedUrlInfo.className, setValue]);

  useEffect(() => {
    // console.log("🔍 RHF defaults:", {
    //   ExperienceTitleDefault: isFixedMode ? "All Classes" : parsedUrlInfo.className || "Boot Camp",
    //   ExperienceDefault: isFixedMode ? "" : "0",
    // });
  }, []);

  /* axios.defaults.headers.post['X-CSRF-Token'] = csrfToken */
  axios.defaults.baseURL = "https://requests.chateau-depierre.com/";
  // Sets the base URL for all API requests to your Bookingmood backend

  const ng = watch("NumberofGuests"); // Guest count
  const occ = watch("Occupancy"); // Room setup
  const ttl = watch("ExperienceTitle"); // Class name
  const cls = watch("Experience"); // Class duration (3-Day or 5-Day)
  const dts = watch("dates"); // Selected calendar dates
  const isPrivate = watch("PrivateExperience"); // Private experience toggle

  const [loaded, setLoaded] = useState(false); // Tracks whether data has been loaded
  const [parentUrl, setParentUrl] = useState(""); // Stores the parent URL (used for parsing experience info)
  const [stayOption, setStayOption] = useState("3D3G1S1D"); // Maps occupancy to Bookingmood stay codes
  const [stayCode, setStayCode] = useState(""); // Bookingmood service ID for selected stay
  const [classDur, setClassDur] = useState(""); // Bookingmood service ID for class duration
  const [privateStayId, setPrivateStayId] = useState<string | null>(null);
  const [numPmts, setnumPmts] = useState(0); // Number of payment installments
  const [taxBreakdown, setTaxBreakdown] = useState([]); // Breakdown of taxes for display
  const [selectedWindow, setSelectedWindow] = useState(null); // Currently selected class window
  const [numNights, setNumNights] = useState(0); // Number of nights for room stays

  useEffect(() => {
    console.log("🔍 selectedWindow UPDATED:", selectedWindow);
    if (selectedWindow) {
      console.log("   junk → freeGroups:", selectedWindow.freeGroups);
      console.log("   → allGroups:", selectedWindow.allGroups);
      console.log("   → className:", selectedWindow.className);
      console.log("   → start:", selectedWindow.start);
      console.log("   → end:", selectedWindow.end);
    } else {
      console.log("   → selectedWindow is NULL");
    }
  }, [selectedWindow]);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  const MAXSTEPS = isRoomMode
    ? isDesktop
      ? 2 // room mode desktop
      : 3 // room mode mobile
    : isDesktop
      ? 3 // class mode desktop
      : 4; // class mode mobile
  const [formStep, setFormStep] = useState(0); // Current step index
  const [checkingOut, setCheckingOut] = useState(false); // Spinner state for checkout
  const [stepsCompleted, setStepsCompleted] = useState([false, false, false, false, false]);
  const stepLabels = isRoomMode
    ? isDesktop
      ? ["Select Suite", "Contact Info", "Checkout"] // desktop room mode
      : ["Select Suite", "Dates", "Contact Info", "Checkout"] // mobile room mode (same labels, but extra step count)
    : isDesktop
      ? ["Select Class", "Contact Info", "Guest Details", "Checkout"] // desktop class mode
      : ["Select Class", "Dates", "Contact Info", "Guest Details", "Checkout"]; // mobile class mode

  const [couponState, setCouponState] = useState<{
    code?: string;
    error?: string;
    discountType?: "percent" | "absolute";
    discountValue?: number;
    duration?: number | null; // 👈 new
  }>({});

  const [calendarReady, setCalendarReady] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // useEffect(() => {
  //   console.log("🧭 [ttl change] ExperienceTitle changed:", {
  //     ttl,
  //     isFixedMode,
  //     isRoomMode,
  //     parsedUrlClassName: parsedUrlInfo.className,
  //   });
  // }, [ttl, isFixedMode, isRoomMode, parsedUrlInfo.className]);

  // NEW: derive selected class metadata from catalog
  const selectedClassMeta = useMemo(() => {
    // console.log("🧠 selectedClassMeta recalculated");
    const key = ttl === "" ? "All Classes" : ttl;
    return CLASS_BY_NAME[key];
  }, [ttl]);

  // useEffect(() => {
  //   console.log("🎯 Class resolution:", {
  //     ttl,
  //     selectedClassMeta,
  //     fuzzyMatchResult: fuzzyMatchClassName(ttl || ""),
  //     allClassNames,
  //   });
  // }, [ttl, selectedClassMeta]);

  const durationLabels = useMemo(() => {
    const custom = (selectedClassMeta as any)?.durationLabels;

    return {
      all: custom?.all ?? "All Class Lengths",
      "0": custom?.["0"] ?? "3-Day Class (4 nights)",
      "1": custom?.["1"] ?? "5-Day Class (6 nights)",
    };
  }, [selectedClassMeta]);

  const selectedSuiteMeta = useMemo(() => {
    // console.log("🧠 selectedSuiteMeta recalculated");
    if (!isRoomMode) return null;
    return SUITE_BY_NAME[parsedUrlInfo.className] || null;
  }, [isRoomMode, parsedUrlInfo.className]);

  const resolvedPricing = useMemo(() => {
    if (isRoomMode) {
      return pricingData.suite.default;
    }

    if (!selectedClassMeta) {
      return pricingData.fixed.all_classes;
    }

    if (ttl === "All Classes" || selectedClassMeta.slug === "all-classes") {
      return isFixedMode ? pricingData.fixed.all_classes : pricingData.regular.all_classes;
    }

    const dayType = selectedClassMeta.dayType;

    if (!dayType) {
      console.warn("Missing dayType for class:", selectedClassMeta);
      return pricingData.fixed.all_classes;
    }

    const bookingChannel = (selectedClassMeta as any).bookingChannel;

    if (bookingChannel && pricingData[bookingChannel]?.[dayType]) {
      return pricingData[bookingChannel][dayType];
    }

    return isFixedMode ? pricingData.fixed[dayType] : pricingData.regular[dayType];
  }, [isRoomMode, isFixedMode, ttl, selectedClassMeta]);

  const handleBrowseClass = (direction: "next" | "prev") => {
    const names = availableClasses.map((c) => c.name);
    const current = ttl;
    const idx = names.indexOf(current);

    if (idx === -1) return;

    const nextIdx = direction === "next" ? (idx + 1) % names.length : (idx - 1 + names.length) % names.length;

    setValue("ExperienceTitle", names[nextIdx], { shouldDirty: true });
  };

  useEffect(() => {
    const guestCount = parseInt(ng || "0", 10);

    // ✅ Only apply in regular booking mode
    if (!isRoomMode && !isFixedMode) {
      if (guestCount >= 3) {
        setValue("PrivateExperience", true, { shouldValidate: true, shouldDirty: true });
      } else {
        setValue("PrivateExperience", false, { shouldValidate: true, shouldDirty: true });
      }
    }
  }, [ng, isRoomMode, isFixedMode, setValue]);

  const handleApplyCoupon = async () => {
    console.log("firing coupon validation");
    const code = getValues("couponCode");

    if (!code) {
      return setCouponState({ error: "Please enter a coupon code." });
    }

    try {
      const res = await axios.get("/validate-coupon", {
        params: { code, product_id: bookingData.product_id },
      });

      if (!res.data.ok) {
        // Backend already sends a reason string
        return setCouponState({ error: res.data.reason || "Coupon could not be applied." });
      }

      console.log("Coupon API response:", res.data.coupon);

      setCouponState({
        code,
        discountType: res.data.coupon.discount_type,
        discountValue: res.data.coupon.discount_value,
        duration: res.data.coupon.duration, // 👈 new
        error: undefined,
      });

      console.log("Coupon applied:", {
        code,
        discountType: res.data.coupon.discount_type,
        discountValue: res.data.coupon.discount_value,
      });

      buildPayload(); // Rebuild payload to include coupon
    } catch (err) {
      // Axios error handling
      let message = "Error validating coupon.";
      if (err.response?.data?.reason) {
        message = err.response.data.reason;
      } else if (err.response?.status === 404) {
        message = "Coupon not found.";
      } else if (err.message) {
        message = `Network error: ${err.message}`;
      }

      setCouponState({ error: message });
    }
  };

  // State for controlling the calendar view
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth()); // 0–11
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());

  // const [guestOptions, setGuestOptions] = useState<number[]>([]);

  const addr_uuid = "13fdc80e-2f34-4a81-9b6c-cf7ca6ac18f3";
  const email_uuid = "52634426-9b7c-44c4-97ee-fd9f83b60ced";
  const phone_uuid = "990f0325-6d2c-4c4c-b97d-0d486acb01d3";
  const name_uuid = "73882436-6b92-4531-81c2-852fe4604f32";
  const allergies_uuid = "462ec2e1-1867-4bc5-af64-a680c24830da";
  const guest_details_uuid = "5336a431-23b0-4c63-9171-525b2318c5de";
  const class_name_uuid = "e42c8c4f-bb61-4654-be84-f52463306036";
  const class_type_uuid = "73273127-9956-48ce-af6d-b0ae576c6cc6";
  const num_guests_uuid = "a3d6cbac-1c3d-4841-b981-aa7877b5f0f5";
  const cap_grp_uuid = "f0a3cd2a-79ca-4271-9390-a19a1a113557";
  const private_parent_uuid = "276bf534-477a-454a-8fc6-79f97d1b2f46";

  // 🧩 Product IDs
  const eventProductId = "50ede6df-d5fe-4818-ac9b-9f19b752c53f"; // Culinary event unit
  // const roomProductIds = {
  //   garden: "143f26e0-b09b-42e6-a4ed-888da1dccb29",
  //   jeanPuy: "a9196928-ed2f-4e25-b18c-69052acf66a5",
  //   hollywood: "d7ab23a9-715f-42e4-abeb-e1d35183c6a4",
  // };

  const guestCount = parseInt(ng || "3", 10);
  const unusedRooms = useMemo(() => getUnusedRoomCount(guestCount), [guestCount, selectedWindow, isFixedMode]);

  const occOptions = {
    1: ["1 Single Room"],
    2: ["1 Double Room", "2 Single Rooms"],
    3: ["1 Single Room / 1 Double Room", "3 Single Rooms"],
    4: ["2 Double Rooms", "2 Single Rooms / 1 Double Room"],
    5: ["1 Single Room / 2 Double Rooms"],
    6: ["3 Double Rooms"],
  };

  const normalizeDate = (d) => {
    const date = new Date(d instanceof Date ? d.getTime() : d);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const bookingData = {
    product_id: "", // dynamically set in buildPayload
    interval: { start: "", end: "" },
    coupon_codes: [], // optional array of coupon codes
    form_values: {}, // mapped form field UUIDs
    occupancy: {}, // { groupId: assignedCount }
    debug: {
      // optional: not sent to API, just for logging
      capacities: {}, // { groupId: maxCapacity }
      productGroups: [], // groupIds tied to chosen product
    },
  };

  function pluralizeRooms(count: number): string {
    return `${count} room${count === 1 ? "" : "s"} left`;
  }

  function applyIncludedTaxes(totalPriceCents, taxRecords, setValue, Euro) {
    const totalPrice = totalPriceCents / 100;
    const includedTaxes = taxRecords?.filter((t) => t.type === "included" && t.percentage > 0) || [];
    const combinedRate = includedTaxes.reduce((sum, tax) => sum + tax.percentage, 0);
    const basePrice = totalPrice / (1 + combinedRate / 100);

    const breakdown = includedTaxes.map((tax) => ({
      label: tax.label?.default || "Unnamed Tax",
      percentage: tax.percentage,
      amount: Math.round(basePrice * (tax.percentage / 100) * 100) / 100,
    }));

    const totalTax = breakdown.reduce((sum, t) => sum + t.amount, 0);

    setValue("optionPrice", Euro.format(totalPrice));
    setValue("VAT", Euro.format(totalTax));
    setTaxBreakdown(breakdown); // ✅ Store for display
  }

  const formData: Record<string, any> = {};
  // Temporary object to hold form field mappings before merging into bookingData

  const sendPayload = useCallback((payload: any) => {
    console.log("here is the payload", JSON.stringify(payload, null, 2));

    console.log("class type before send:", {
      value: payload?.form_values?.[class_type_uuid],
      class_type_uuid,
    });

    const cleanPayload = { ...payload };
    delete cleanPayload.debug;

    setCheckingOut(true);
    console.log("Checking out", checkingOut);

    axios
      .post("/book", cleanPayload)
      .then((response) => {
        console.log("Response data:", JSON.stringify(response.data, null, 2));
        console.log("Checkout complete", checkingOut);

        setTimeout(() => {
          if (response.data?.payment_url) {
            window.location.replace(response.data.payment_url);
          } else {
            console.warn("No payment_url returned in response:", response.data);
          }
        }, 3000);
      })
      .catch((error) => {
        console.error("Client-side error:", error.message);

        if (error.response?.data) {
          console.error("Server returned error:", JSON.stringify(error.response.data, null, 2));
        }
      });
  }, []);

  const taxQuery = useQuery({
    queryKey: ["taxes"],
    queryFn: async () => {
      // console.log("➡️ Fetching /taxes …");
      try {
        const response = await axios.get("/taxes");
        // console.log("✅ /taxes response:", response.data);
        return response.data;
      } catch (error) {
        console.error("❌ /taxes error:", error);
        throw error;
      }
    },
    refetchInterval: 5 * 60 * 1000,
  });

  const srvQuery = useQuery({
    queryKey: ["prices"],
    queryFn: async () => {
      // console.log("➡️ Fetching /services …");
      try {
        const response = await axios.get("/services");
        // console.log("✅ /services response:", response.data);
        return response.data;
      } catch (error) {
        console.error("❌ /services error:", error);
        throw error;
      }
    },
    refetchInterval: 5 * 60 * 1000,
  });

  const productGroupMap = useMemo(() => {
    // console.log("🧠 productGroupMap recalculated");
    const data = Array.isArray(productCapacityMapQuery?.data) ? productCapacityMapQuery.data : [];

    return data.reduce(
      (acc, { productId, groupId }) => {
        if (!acc[productId]) acc[productId] = [];
        if (!acc[productId].includes(groupId)) acc[productId].push(groupId);
        return acc;
      },
      {} as Record<string, string[]>,
    );
  }, [productCapacityMapQuery?.data]);

  // 🔧 groupId → groupName (already have)
  const groupNameMap = useMemo(() => {
    const data = Array.isArray(productCapacityMapQuery?.data) ? productCapacityMapQuery.data : [];

    return data.reduce(
      (acc, { groupId, groupName }) => {
        acc[groupId] = groupName || "Unnamed Group";
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [productCapacityMapQuery?.data]);

  // 🔧 all known groupIds (already have)
  const allKnownGroupIds = useMemo(() => {
    // console.log("🧠 allKnownGroupIds recalculated");
    const data = Array.isArray(productCapacityMapQuery?.data) ? productCapacityMapQuery.data : [];

    return Array.from(new Set(data.map((x) => x.groupId)));
  }, [productCapacityMapQuery?.data]);

  // 🔧 NEW: groupId → { min, max }
  const groupCapacityMap = useMemo(() => {
    // console.log("🧠 groupCapacityMap recalculated");
    const data = Array.isArray(productCapacityMapQuery?.data) ? productCapacityMapQuery.data : [];

    return data.reduce(
      (acc, { groupId, min, max }) => {
        acc[groupId] = { min: min ?? 0, max: max ?? 0 };
        return acc;
      },
      {} as Record<string, { min: number; max: number }>,
    );
  }, [productCapacityMapQuery?.data]);

  const suiteProductIds = useMemo(() => {
    // console.log("🧠 suiteProductIds recalculated");
    const data = Array.isArray(productCapacityMapQuery?.data) ? productCapacityMapQuery.data : [];

    const suiteNames = ["garden suite", "jean puy suite", "hollywood suite"];

    return data
      .filter(({ productName }) => suiteNames.some((suite) => productName?.toLowerCase().includes(suite)))
      .map(({ productId, productName }) => ({
        productId,
        productName,
      }));
  }, [productCapacityMapQuery?.data]);

  const currentProductImages = useMemo(() => {
    if (!isRoomMode || !productCapacityMapQuery.data) return [];

    // 1. What suite name is currently selected?
    const selectedSuiteName = watch("ExperienceTitle");

    // 2. Find the suite object from suiteProductIds
    const selectedSuite = suiteProductIds.find((s) => s.productName === selectedSuiteName);

    // 3. Determine which productId to use
    const productIdToUse = selectedSuite?.productId || parsedUrlInfo.productId;

    // 4. Find the product entry in Bookingmood data
    const entry = productCapacityMapQuery.data.find((p) => p.productId === productIdToUse);

    return entry?.images || [];
  }, [isRoomMode, suiteProductIds, watch("ExperienceTitle"), parsedUrlInfo.productId, productCapacityMapQuery.data]);

  const blockedSuites = useMemo(() => {
    if (!dateQuery.data || !bookingDetailsQuery.data) return [];
    const today = normalizeDate(new Date());

    const suiteRanges: { startDate: Date; endDate: Date }[] = [];
    const suiteNames = ["Jean Puy Suite", "Garden Suite", "Hollywood Suite"];

    (dateQuery.data ?? [])
      .filter((e) => e.type === "booking" && e.booking_id)
      .forEach((e) => {
        const start = normalizeDate(parseDateOnlyToLocal(e.start_date));
        const end = normalizeDate(parseDateOnlyToLocal(e.end_date));
        if (normalizeDate(end) < today) return;

        // Look up class name from bookingDetails, same as enrichment
        const nameDetail = (bookingDetailsQuery.data ?? []).find((d) => d.booking_id === e.booking_id && d.service_id === class_name_uuid);
        const className = nameDetail?.value || fuzzyMatchClassName(e.title || "Unknown");

        if (suiteNames.some((name) => className.toLowerCase().includes(name.toLowerCase()))) {
          suiteRanges.push({ startDate: start, endDate: end });
        }
      });

    // console.log(
    //   "🚫 Blocked suite ranges:",
    //   suiteRanges.map((r) => `${r.startDate.toDateString()} → ${r.endDate.toDateString()}`),
    // );
    return suiteRanges;
  }, [dateQuery.data, bookingDetailsQuery.data]);

  const fixedWindows = useMemo(() => {
    return (dateQuery.data ?? [])
      .filter((e) => e.type === "note" && e.title?.startsWith("Fixed - "))
      .map((entry) => {
        const rawName = entry.title.replace("Fixed -", "").trim();
        const className = fuzzyMatchClassName(rawName);

        // Keep only while debugging fixed-name resolution
        // console.log("🧩 Fixed window name resolution:", {
        //   backendTitle: entry.title,
        //   rawName,
        //   className,
        //   allCatalogClassNames,
        // });

        const duration = classDurationMap[className] || "0";
        const start = normalizeDate(parseDateOnlyToLocal(entry.start_date));
        const end = normalizeDate(parseDateOnlyToLocal(entry.end_date));

        return { className, duration, start, end };
      });
  }, [dateQuery.data, classDurationMap, allCatalogClassNames]);

  const availableClasses = useMemo(() => {
    if (isRoomMode) return [];

    if (isFixedMode) {
      const names = Array.from(new Set(fixedWindows.map((w) => w.className)));
      return names.map((name) => CLASS_BY_NAME[name]).filter(Boolean);
    }

    // Regular mode → all classes
    return CLASS_CATALOG;
  }, [isRoomMode, isFixedMode, fixedWindows]);

  // console.log("Fixed class windows:", fixedWindows);
  useEffect(() => {
    if (isRoomMode) {
      setValue("ExperienceTitle", parsedUrlInfo.className, { shouldValidate: true });
    }
  }, [isRoomMode, parsedUrlInfo.className, setValue]);

  useEffect(() => {
    if (isRoomMode && numNights > 0) {
      setValue("Experience", numNights.toString(), { shouldValidate: true });
    }
  }, [isRoomMode, numNights, setValue]);

  useEffect(() => {
    if (isRoomMode) return;
    if (!selectedWindow) return;

    // ⬅️ NEW: ensure class metadata exists before overwriting ExperienceTitle
    const meta = CLASS_BY_NAME[selectedWindow.className];
    if (!meta) return; // prevents early overwrite on fixed pages

    const start = normalizeDate(selectedWindow.start);
    const end = normalizeDate(selectedWindow.end);

    const current = getValues("dates");
    const same = Array.isArray(current) && current[0]?.getTime?.() === start.getTime() && current[1]?.getTime?.() === end.getTime();

    if (same) return;

    // Only overwrite class name if not All Classes
    if (ttl !== "All Classes") {
      setValue("ExperienceTitle", selectedWindow.className, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    // Only force class length in FIXED MODE
    if (isFixedMode) {
      setValue("Experience", selectedWindow.duration, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    setValue("dates", [start, end], {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    });

    jumpToDate(start);
  }, [selectedWindow, isRoomMode, isFixedMode, ttl, setValue, getValues]);

  useEffect(() => {
    if (!isFixedMode) return;

    // Run only on first load
    if (!loaded) {
      if (parsedUrlInfo.className) {
        setValue("ExperienceTitle", parsedUrlInfo.className, { shouldValidate: true });
      }

      if (parsedUrlInfo.className === "All Classes") {
        setValue("Experience", "", { shouldValidate: true });
      } else if (parsedUrlInfo.classLen) {
        setValue("Experience", parsedUrlInfo.classLen, { shouldValidate: true });
      }

      setLoaded(true);
    }
  }, [isFixedMode, parsedUrlInfo.className, parsedUrlInfo.classLen, setValue, loaded]);

  const enrichedWindows = useMemo(() => {
    if (!dateQuery.data || !bookingsQuery.data || !productCapacityMapQuery.data || !bookingDetailsQuery.data) {
      // console.warn("⚠️ Missing data for enrichment. Skipping enrichedWindows.");
      return [];
    }

    if (!productGroupMap[eventProductId] || productGroupMap[eventProductId].length === 0) {
      // console.warn("Mismatch: eventProductId not found in capacity map or has no groups");
    }

    if (parsedUrlInfo.isRoomMode && !parsedUrlInfo.productId) {
      // console.warn("Room mode: could not resolve productId from slug/name");
    }

    const today = normalizeDate(new Date());

    const bookingMap: Record<string, Record<string, number>> = Object.fromEntries(
      (bookingsQuery.data ?? []).map((b) => [b.id, b.occupancy || {}]),
    );

    const classNameMap: Record<string, string> = Object.fromEntries(
      (bookingDetailsQuery.data ?? [])
        .filter((detail) => detail.service_id === class_name_uuid && detail.booking_id)
        .map((detail) => [detail.booking_id!, fuzzyMatchClassName(detail.value)]),
    );

    const windowKey = (start: Date, end: Date) => `${normalizeDate(start).getTime()}-${normalizeDate(end).getTime()}`;

    const windowOccupancyMap = new Map<string, Record<string, number>>();
    (dateQuery.data ?? [])
      .filter((e) => e.type === "booking" && e.booking_id)
      .forEach((e) => {
        const start = normalizeDate(parseDateOnlyToLocal(e.start_date));
        const end = normalizeDate(parseDateOnlyToLocal(e.end_date));
        if (normalizeDate(end) < today) return;

        const key = windowKey(start, end);
        const occupancy = bookingMap[e.booking_id!] || {};

        if (!windowOccupancyMap.has(key)) windowOccupancyMap.set(key, {});
        const agg = windowOccupancyMap.get(key)!;

        Object.entries(occupancy).forEach(([groupId, count]) => {
          const maxCap = groupCapacityMap[groupId]?.max ?? 2;
          const incoming = count || 0;
          const current = agg[groupId] || 0;

          if (current + incoming > maxCap) {
            console.warn(`❌ Prevented reuse of group ${groupId}: current=${current}, incoming=${incoming}, max=${maxCap}`);
            return;
          }
          agg[groupId] = current + incoming;
        });
      });

    function computeAvailability(occupancy: Record<string, number>, _productId: string, isPrivate: boolean) {
      if (isPrivate) {
        return { allGroups: [], freeGroups: [], remainingRooms: 0, isAvailable: false, status: "full" };
      }

      const allGroups = Object.keys(occupancy);
      const freeGroups = allGroups.filter((id) => occupancy[id] === 0);
      const remainingRooms = freeGroups.length;
      const isAvailable = remainingRooms > 0;

      return {
        allGroups,
        freeGroups,
        remainingRooms,
        isAvailable,
        status: isAvailable ? "available" : "full",
      };
    }

    const fixedKeys = new Set<string>();
    const fixed = (dateQuery.data ?? [])
      .filter((e) => e.type === "note" && e.title?.startsWith("Fixed - "))
      .map((entry) => {
        const rawName = entry.title!.replace("Fixed -", "").trim();
        const className = fuzzyMatchClassName(rawName);
        const start = normalizeDate(parseDateOnlyToLocal(entry.start_date));
        const end = normalizeDate(parseDateOnlyToLocal(entry.end_date));
        const rangeKey = `${start.getTime()}-${end.getTime()}`;
        fixedKeys.add(rangeKey);

        const windowId = windowKey(start, end);
        const rawOccupancy = windowOccupancyMap.get(windowId) || {};
        const occupancy = Object.fromEntries(allKnownGroupIds.map((id) => [id, rawOccupancy[id] || 0]));
        const availability = computeAvailability(occupancy, eventProductId, false);

        return {
          className,
          duration: classDurationMap[className] ?? "0",
          start,
          end,
          occupancy,
          booking_id: null,
          ...availability,
          isFixed: true,
          productId: eventProductId,
        };
      });

    const regularWindowMap = new Map<string, any>();
    (dateQuery.data ?? [])
      .filter((e) => {
        if (e.type !== "booking" || !e.booking_id) return false;
        const start = normalizeDate(parseDateOnlyToLocal(e.start_date));
        const end = normalizeDate(parseDateOnlyToLocal(e.end_date));
        const notInPast = normalizeDate(end) >= today;
        const rangeKey = `${start.getTime()}-${end.getTime()}`;
        return notInPast && !fixedKeys.has(rangeKey);
      })
      .forEach((e) => {
        const start = normalizeDate(parseDateOnlyToLocal(e.start_date));
        const end = normalizeDate(parseDateOnlyToLocal(e.end_date));
        const windowId = windowKey(start, end);

        if (!regularWindowMap.has(windowId)) {
          const productId = "unknown"; // bookings don't carry product_id reliably
          const rawOccupancy = windowOccupancyMap.get(windowId) || {};
          const occupancy = Object.fromEntries(allKnownGroupIds.map((id) => [id, rawOccupancy[id] || 0]));

          const privDetail = (bookingDetailsQuery.data ?? []).find(
            (d) => d.booking_id === e.booking_id && d.service_id === private_parent_uuid,
          );

          const isPrivate = privDetail && !["standard"].includes(String(privDetail.value).toLowerCase());

          // console.log("Privacy check:", {
          //   bookingId: e.booking_id,
          //   rawValue: privDetail?.value,
          //   isPrivate,
          // });

          const className = classNameMap[e.booking_id!] || fuzzyMatchClassName(e.title || "Unknown");
          // const className = fuzzyMatchClassName(e.title || "Unknown");

          // 🚫 Skip suites by name
          const suiteNames = ["Jean Puy Suite", "Garden Suite", "Hollywood Suite"];
          if (suiteNames.some((s) => className.toLowerCase().includes(s.toLowerCase()))) {
            // console.log("Skipping suite booking by name:", className);
            return;
          }

          const availability = computeAvailability(occupancy, productId, isPrivate);

          regularWindowMap.set(windowId, {
            className,
            productId,
            duration: classDurationMap[className] ?? "0",
            start,
            end,
            occupancy,
            booking_id: e.booking_id,
            isPrivate,
            ...availability,
            isFixed: false,
          });
        }
      });

    const combined = [...fixed, ...regularWindowMap.values()];

    console.log("✅ Final Combined Enriched Windows:");
    combined.forEach((w, i) => {
      console.log(
        `[#${i + 1}] ${w.className} (${w.start.toDateString()} → ${w.end.toDateString()})` +
          ` → ${w.remainingRooms} rooms left, status=${w.status}, productId=${w.productId}, bookingId=${w.booking_id}, isPrivate=${
            w.isPrivate ? "true" : "false"
          }`,
      );
    });

    return combined;
  }, [dateQuery.data, bookingsQuery.data, productCapacityMapQuery.data, bookingDetailsQuery.data]);

  // useEffect(() => {
  //   console.log("🧱 Enriched windows:", enrichedWindows);
  // }, [enrichedWindows]);

  function case1Behavior(enrichedWindows, ttl, today, cutoff) {
    // console.log("[Case 1] Specific class page or Case 3 + specific class", { ttl });

    return enrichedWindows.filter((w) => {
      const notStarted = normalizeDate(w.start) > today;
      const isAvailable = w.isAvailable;
      const isOpen = w.className === "Booking" || w.className === "Unknown";
      const outsideBuffer = normalizeDate(w.start) >= cutoff;

      const include = isAvailable && notStarted && outsideBuffer && (w.className === ttl || isOpen);

      // console.log("[Case 1 filter]", w.className, {
      //   notStarted,
      //   isAvailable,
      //   isOpen,
      //   outsideBuffer,
      //   include,
      // });
      return include;
    });
  }

  function case2Behavior(enrichedWindows, ttl, today, cutoff) {
    return enrichedWindows.filter((w) => {
      const notStarted = normalizeDate(w.start) > today;
      const isAvailable = w.isAvailable;
      const isOpen = w.className === "Booking" || w.className === "Unknown";
      const outsideBuffer = normalizeDate(w.start) >= cutoff;

      // Explicitly treat "" and "All Classes" as "include everything"
      const isAllClasses = ttl === "" || ttl === undefined || ttl === "All Classes";
      const includeClass = isAllClasses || w.className === ttl;

      const include = isAvailable && notStarted && outsideBuffer && !isOpen && includeClass;
      return include;
    });
  }

  const availableWindows = useMemo(() => {
    if (!enrichedWindows.length) return [];
    const today = normalizeDate(new Date());
    const cutoff = normalizeDate(add_days(today, parsedUrlInfo.isRoomMode ? 2 : 14));
    const isAllClasses = !ttl || ttl === "All Classes";

    const base =
      isPrescheduledPage || isAllClasses
        ? case2Behavior(enrichedWindows, ttl, today, cutoff)
        : case1Behavior(enrichedWindows, ttl, today, cutoff);

    // ✅ additionally filter by class length
    return base.filter((w) => !cls || w.duration === cls);
  }, [enrichedWindows, ttl, cls, isPrescheduledPage]);

  // 1. derive dats first
  const dats = useMemo(() => {
    if (!dateQuery.data) return [];
    return (dateQuery.data ?? []).map(({ start_date, end_date, padding }) => {
      const sd = parseDateOnlyToLocal(start_date);
      const ed = parseDateOnlyToLocal(end_date);
      const pd = parseInt(padding) || 0;
      return { startDate: sd, endDate: add_days(ed, pd) };
    });
  }, [dateQuery.data]);

  function carveOutsCase1(enrichedWindows, ttl, today, cutoff) {
    // console.log("[CarveOuts Case 1]", { ttl });
    return enrichedWindows.filter((w) => {
      const notStarted = normalizeDate(w.start) > today;
      const isAvailable = w.isAvailable;
      const isOpen = w.className === "Booking" || w.className === "Unknown";
      const outsideBuffer = normalizeDate(w.start) >= cutoff;

      const include = isAvailable && notStarted && outsideBuffer && (w.className === ttl || isOpen);
      // console.log("[Case 1 carve]", w.className, { notStarted, isAvailable, isOpen, outsideBuffer, include });
      return include;
    });
  }

  function isAllClassesSelected(name?: string) {
    return !name || name === "All Classes";
  }

  function carveOutsCase2(enrichedWindows, ttl, today, cutoff) {
    // console.log("[CarveOuts Case 2]", { ttl });
    const isAll = isAllClassesSelected(ttl);

    return enrichedWindows.filter((w) => {
      const notStarted = normalizeDate(w.start) > today;
      const isAvailable = w.isAvailable;
      const isOpen = w.className === "Booking" || w.className === "Unknown";
      const outsideBuffer = normalizeDate(w.start) >= cutoff;

      // ✅ If "All Classes" → include everything
      // ✅ If specific class selected → include only that class
      const includeClass = isAll ? true : w.className === ttl;

      const include = isAvailable && notStarted && outsideBuffer && !isOpen && includeClass;

      // console.log("[Case 2 carve]", w.className, { notStarted, isAvailable, isOpen, outsideBuffer, include });
      return include;
    });
  }

  const filteredBlockedDates = useMemo(() => {
    // console.log("[Mode Flags]", {
    //   isPrescheduledPage,
    //   ttl,
    //   isAllClasses: !ttl || ttl === "All Classes",
    // });

    if (!enrichedWindows.length) return [];

    const out: { startDate: Date; endDate: Date }[] = [];
    const today = normalizeDate(new Date());
    const cutoff = normalizeDate(add_days(today, 14));
    const isAllClasses = !ttl || ttl === "All Classes";

    // console.log("[filteredBlockedDates mode]", { isPrescheduledPage, isAllClasses, ttl });

    // 1) Backend blocked periods
    (dateQuery.data ?? [])
      .filter((e) => e.type === "blocked period" || e.type === "blocked-period")
      .forEach((e) => {
        const start = normalizeDate(parseDateOnlyToLocal(e.start_date));
        const end = normalizeDate(parseDateOnlyToLocal(e.end_date));
        if (start <= end) out.push({ startDate: start, endDate: end });
      });

    // 2) Always block the 14-day buffer zone
    const bufferStart = today;
    const bufferEnd = normalizeDate(add_days(cutoff, -1));
    if (bufferStart <= bufferEnd) out.push({ startDate: bufferStart, endDate: bufferEnd });

    // 2b) Suites: hard-block, never enrich
    blockedSuites.forEach(({ startDate, endDate }) => {
      out.push({ startDate: normalizeDate(startDate), endDate: normalizeDate(endDate) });
    });

    // 3) Carve-outs
    const carveOuts =
      isPrescheduledPage || isAllClassesSelected(ttl)
        ? carveOutsCase2(enrichedWindows, ttl, today, cutoff)
        : carveOutsCase1(enrichedWindows, ttl, today, cutoff);

    // 4) Block everything else (excluding carved)
    enrichedWindows.forEach((w) => {
      const carved = carveOuts.some(
        (c) =>
          normalizeDate(c.start).getTime() === normalizeDate(w.start).getTime() &&
          normalizeDate(c.end).getTime() === normalizeDate(w.end).getTime() &&
          c.className === w.className,
      );
      if (carved) return;

      const start = normalizeDate(w.start);
      const end = normalizeDate(w.end);
      const isKnownClass = allCatalogClassNames.includes(w.className);
      const isSuite = suiteProductIds.some((suite) => suite.productId === w.productId);

      // Suites are always blocked regardless of mode or availability
      if (isSuite) {
        out.push({ startDate: start, endDate: end });
        // console.log("[Block Suites]", w.className, { pushed: true });
        return;
      }

      // 🔥 NEW RULE: Block any class not in the official class list
      if (!isKnownClass) {
        out.push({ startDate: start, endDate: end });
        return;
      }

      if (isPrescheduledPage || isAllClasses) {
        if (w.status === "full") {
          if (w.className === ttl) {
            // Same class → skip blocking so it renders rose
            // console.log("[Skip blocking full window for current class]", w.className);
          } else {
            // Different class → treat as blocked (grey)
            out.push({ startDate: start, endDate: end });
            // console.log("[Block full window from other class]", w.className);
          }
        } else {
          out.push({ startDate: start, endDate: end });
          // console.log("[Block Case 2]", w.className, { isOpen, pushed: true });
        }
      } else {
        if (!w.isAvailable) {
          if (w.status === "full") {
            if (isRoomMode) {
              // In room mode, block full windows
              out.push({ startDate: start, endDate: end });
              // console.log("[RoomMode] Block full window Case 1", w.className);
            } else if (w.className !== ttl) {
              // On other class pages, block full windows from different classes
              out.push({ startDate: start, endDate: end });
              // console.log("[Block full window from other class Case 1]", w.className);
            } else {
              // Same class → skip so it renders rose
              // console.log("[Skip blocking full window Case 1]", w.className);
            }
          } else {
            out.push({ startDate: start, endDate: end });
            // console.log("[Block Case 1] unavailable", w.className);
          }
        } else if (w.isFixed && w.className !== ttl) {
          out.push({ startDate: start, endDate: end });
          // console.log("[Block Case 1] other-class fixed", w.className);
        }
      }
    });

    const scheduled = enrichedWindows
      .filter((w) => normalizeDate(w.start) > today)
      .sort((a, b) => normalizeDate(a.start).getTime() - normalizeDate(b.start).getTime());
    // console.log(
    //   "[Scheduled windows]",
    //   scheduled.map((w) => `${w.start.toDateString()} → ${w.end.toDateString()}`)
    // );
    // 5) Block open gaps and terminal tail
    if (isPrescheduledPage || isAllClasses) {
      // Head block: buffer → first scheduled
      if (scheduled.length > 0) {
        const firstStart = normalizeDate(scheduled[0].start);
        const headStart = cutoff;
        const headEnd = normalizeDate(new Date(firstStart.getTime() - 86400000));
        if (headStart <= headEnd) {
          out.push({ startDate: headStart, endDate: headEnd });
          // console.log("[Head open window blocked]", headStart.toDateString(), "→", headEnd.toDateString());
        }
      }

      // Block gaps between scheduled classes
      for (let i = 0; i < scheduled.length - 1; i++) {
        const endOfCurrent = normalizeDate(scheduled[i].end);
        const startOfNext = normalizeDate(scheduled[i + 1].start);

        // Gap starts the day after current ends
        const gapStart = normalizeDate(new Date(endOfCurrent.getTime() + 86400000));
        // Gap ends the day before next starts
        const gapEnd = normalizeDate(new Date(startOfNext.getTime() - 86400000));

        if (gapStart <= gapEnd) {
          out.push({ startDate: gapStart, endDate: gapEnd });
          // console.log("[Gap blocked]", gapStart.toDateString(), "→", gapEnd.toDateString());
        }
      }

      // Block terminal tail out to 2050
      if (scheduled.length > 0) {
        const lastEnd = normalizeDate(scheduled[scheduled.length - 1].end);
        const terminalStart = normalizeDate(new Date(lastEnd.getTime() + 86400000));
        const terminalEnd = normalizeDate(parseDateOnlyToLocal("2050-12-31"));

        if (terminalStart <= terminalEnd) {
          out.push({ startDate: terminalStart, endDate: terminalEnd });
          console.log("[Terminal open window blocked]", terminalStart.toDateString(), "→", terminalEnd.toDateString());
        }
      } else {
        // No scheduled classes at all → block everything from today to 2050
        const terminalStart = today;
        const terminalEnd = normalizeDate(parseDateOnlyToLocal("2050-12-31"));
        out.push({ startDate: terminalStart, endDate: terminalEnd });
        console.log("[No classes → block entire tail]", terminalStart.toDateString(), "→", terminalEnd.toDateString());
      }
    }

    // 7) Merge ranges
    function mergeRanges(ranges: { startDate: Date; endDate: Date }[]) {
      if (!ranges.length) return ranges;
      const sorted = ranges
        .map((r) => ({ startDate: normalizeDate(r.startDate), endDate: normalizeDate(r.endDate) }))
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

      const merged: { startDate: Date; endDate: Date }[] = [];
      let current = { ...sorted[0] };
      for (let i = 1; i < sorted.length; i++) {
        const next = sorted[i];
        const currentEndPlusOne = current.endDate.getTime() + 86400000;
        if (next.startDate.getTime() <= currentEndPlusOne) {
          current.endDate = next.endDate.getTime() > current.endDate.getTime() ? next.endDate : current.endDate;
        } else {
          merged.push(current);
          current = { ...next };
        }
      }
      merged.push(current);
      return merged;
    }

    const merged = mergeRanges(out);

    // 8) Carve out selected range if valid (exclude suites from selection)
    let selStart: Date | null = null;
    let selEnd: Date | null = null;
    if (selectedWindow) {
      selStart = normalizeDate(selectedWindow.start);
      selEnd = normalizeDate(selectedWindow.end);
    } else {
      const dates = getValues("dates");
      if (Array.isArray(dates) && dates.length === 2) {
        selStart = normalizeDate(dates[0]);
        selEnd = normalizeDate(dates[1]);
      }
    }

    const windowsForFilter =
      isPrescheduledPage || isAllClasses
        ? carveOutsCase2(enrichedWindows, ttl, today, cutoff)
        : carveOutsCase1(enrichedWindows, ttl, today, cutoff);

    const match = windowsForFilter.find(
      (w) => normalizeDate(w.start).getTime() === selStart?.getTime() && normalizeDate(w.end).getTime() === selEnd?.getTime(),
    );

    const isSuiteSelection = match ? suiteProductIds.some((suite) => suite.productId === match.productId) : false;

    const isValidSelection =
      !!match &&
      !isSuiteSelection &&
      match.isAvailable &&
      normalizeDate(match.start) > today &&
      (!isPrescheduledPage ? normalizeDate(match.start) >= cutoff : true);

    if (isValidSelection) {
      return merged.filter(
        (r) => normalizeDate(r.startDate).getTime() !== selStart!.getTime() || normalizeDate(r.endDate).getTime() !== selEnd!.getTime(),
      );
    }

    return merged;
  }, [dateQuery.data, enrichedWindows, ttl, selectedWindow, getValues, isPrescheduledPage]);

  // Utility stays the same
  const dayKey = (d: Date) => toLocalYMD(normalizeDate(d));

  // useEffect(() => {
  //   if (!selectedWindow) return;

  //   const start = normalizeDate(selectedWindow.start);
  //   const end = normalizeDate(selectedWindow.end);

  //   // Check current form values
  //   const current = getValues("dates");
  //   const same = Array.isArray(current) && current[0]?.getTime?.() === start.getTime() && current[1]?.getTime?.() === end.getTime();

  //   if (!same) {
  //     setValue("ExperienceTitle", selectedWindow.className, { shouldValidate: true });
  //     setValue("Experience", selectedWindow.duration, { shouldValidate: true });
  //     setValue("dates", [start, end], {
  //       shouldTouch: true,
  //       shouldDirty: true,
  //       shouldValidate: true,
  //     });
  //     jumpToDate(start);
  //   }
  // }, [selectedWindow, setValue, getValues]);

  // console.log("Blocked ranges:", filteredBlockedDates);

  const paySchedule = useMemo(() => {
    const services = srvQuery.data || [];
    const schedule: any[] = [];

    const extractInstallmentValue = (entry: any) => {
      const raw = entry?.value?.value;

      // New Bookingmood shape can be either:
      // value: 25
      // or value: [{ value: 25 }]
      if (Array.isArray(raw)) {
        return raw[0]?.value;
      }

      return raw;
    };

    const pushMoment = (entry: any, serviceId: string, source: string) => {
      schedule.push({
        mmnt: entry.moment?.type,
        offset: entry.moment?.offset,
        vl: extractInstallmentValue(entry),
        svc: serviceId,
        source,
      });
    };

    services.forEach((svc: any) => {
      const serviceId = svc.id;

      // ✅ New Bookingmood API shape
      svc.fee?.installments?.forEach((entry: any) => {
        pushMoment(entry, serviceId, "fee.installments");
      });

      // Keep this in case Bookingmood uses deposit installments later
      svc.deposit?.installments?.forEach((entry: any) => {
        pushMoment(entry, serviceId, "deposit.installments");
      });

      // Backward compatibility with old shapes
      svc.fee?.payment_moments?.forEach((entry: any) => {
        pushMoment(entry, serviceId, "fee.payment_moments");
      });

      svc.fee?.deposit_moments?.forEach((entry: any) => {
        pushMoment(entry, serviceId, "fee.deposit_moments");
      });

      svc.service_payment_moments?.forEach((entry: any) => {
        pushMoment(entry, serviceId, "service_payment_moments");
      });
    });

    console.table(schedule);
    return schedule;
  }, [srvQuery.data]);

  const servs = useMemo(() => {
    if (!Array.isArray(srvQuery.data)) return [];

    return srvQuery.data.map((srv) => {
      const lbl = srv?.label?.default ?? "Unnamed";
      const svc_id = srv?.id ?? "unknown";

      // NEW: correct path to the raw price in cents
      const prc = srv?.fee?.price?.value?.[0]?.value ?? null;

      return { id: svc_id, label: lbl, price: prc };
    });
  }, [srvQuery.data]);

  const guestOptions = useMemo(() => {
    if (parsedUrlInfo.isRoomMode) {
      return [1, 2];
    }

    // Prescheduled page → min 1 always
    const min = isFixedMode || selectedWindow ? 1 : 2;

    let max = 6;
    if (selectedWindow) {
      max = Math.min(6, selectedWindow.remainingRooms * 2);
    }

    return Array.from({ length: max - min + 1 }, (_, i) => i + min);
  }, [selectedWindow, isFixedMode]);

  const availableDurations = useMemo(() => {
    if (isSpecificFixedClassPage) {
      return parsedUrlInfo.classLen ? [parsedUrlInfo.classLen] : [];
    }

    if (!isFixedMode) return ["0", "1"];

    const durations = new Set(enrichedWindows.filter((w) => w.isFixed && w.isAvailable).map((w) => w.duration));
    return Array.from(durations);
  }, [enrichedWindows, isFixedMode, isSpecificFixedClassPage, parsedUrlInfo.classLen]);

  useEffect(() => {
    if (guestOptions.length === 0) {
      // console.log("⚠️ guestOptions empty, skipping clamp");
      return;
    }

    const currentGuests = parseInt(getValues("NumberofGuests") ?? "0", 10);
    const safeGuests = guestOptions.includes(currentGuests) ? currentGuests : guestOptions[0];

    // console.log("🔍 Clamping NumberofGuests:", {
    //   currentGuests,
    //   safeGuests,
    //   guestOptions,
    // });

    setValue("NumberofGuests", String(safeGuests), { shouldValidate: true });

    const defaultOcc = occOptions[safeGuests]?.[0];
    // console.log("🔍 Setting Occupancy:", defaultOcc);

    if (defaultOcc) {
      setValue("Occupancy", defaultOcc, { shouldValidate: true });
    }
  }, [guestOptions, ng]);

  // Build reserved list as "everything except availableWindows"

  useEffect(() => {
    if (isPrescheduledPage) return; // 🚫 skip parsing in Case 2

    if (!parsedUrlInfo || parsedUrlInfo.isGlobalSearch) return;

    if (parsedUrlInfo.className) {
      setValue("ExperienceTitle", parsedUrlInfo.className, { shouldValidate: true });
    }
    if (parsedUrlInfo.classLen) {
      setValue("Experience", parsedUrlInfo.classLen, { shouldValidate: true });
    }
  }, []); // ✅ run once only

  useEffect(() => {
    // 🚫 Do not auto-extend in room mode
    if (parsedUrlInfo.isRoomMode) return;

    const currentDates = getValues("dates");
    if (Array.isArray(currentDates) && currentDates.length === 2 && !selectedWindow) {
      const start = normalizeDate(currentDates[0]);
      const defaultDuration = cls === "1" ? 6 : 4; // 5-day = 6 nights, 3-day = 4 nights
      const newEnd = normalizeDate(add_days(start, defaultDuration));
      setValue("dates", [start, newEnd], { shouldValidate: true });
    }
  }, [cls, selectedWindow, getValues, setValue]);

  const watchedDates = watch("dates");

  function handleResetFilters() {
    // Reset both dropdowns
    setValue("ExperienceTitle", "All Classes", { shouldValidate: true, shouldDirty: true }); // "" maps to "All Classes"
    setValue("Experience", "", { shouldValidate: true, shouldDirty: true }); // "" maps to "All Class Lengths"

    // Clear any selected window
    setSelectedWindow(null);

    // Clear dates
    setValue("dates", [], { shouldValidate: true, shouldDirty: true });
  }

  function rgbToHex(rgbString) {
    const [r, g, b] = rgbString.match(/\d+/g).map(Number);
    return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
  }

  const selectedClassName = watch("ExperienceTitle");
  // Utility: convert rgb(...) to hex
  function rgbToHex(rgb: string): string {
    const result = rgb.match(/\d+/g);
    if (!result) return "#ffffff";
    return (
      "#" +
      result
        .slice(0, 3)
        .map((x) => ("0" + parseInt(x).toString(16)).slice(-2))
        .join("")
    );
  }

  // Utility: compute luminance and return calibrated text color
  function getTextColor(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const [R, G, B] = [r, g, b].map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
    const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;

    if (luminance > 0.65) return "#000"; // lightest → black text
    if (luminance > 0.45) return "#555"; // middle → medium gray text
    return "#eee"; // darkest → soft light text
  }

  useEffect(() => {
    if (!enrichedWindows.length || !bookingsQuery.data || !productCapacityMapQuery.data || !dateQuery.data) {
      return;
    }

    requestAnimationFrame(() => {
      const els = document.querySelectorAll(".calendar__day-container");
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      els.forEach((el) => {
        const label = el.getAttribute("aria-label");
        if (!label) return;

        const [month, day, year] = label.split("/").map(Number);
        const date = new Date(year, month - 1, day);
        date.setHours(0, 0, 0, 0);

        // Reset attributes
        ["data-status", "data-type", "data-shade", "data-range", "title"].forEach((attr) => el.removeAttribute(attr));

        const content = el.querySelector(".calendar__day-content");
        if (content) content.style.color = "";

        // 🚫 Blocked ranges win, always (including suites)
        const isBlocked = filteredBlockedDates.some(
          ({ startDate, endDate }) =>
            date.getTime() >= normalizeDate(startDate).getTime() && date.getTime() <= normalizeDate(endDate).getTime(),
        );

        if (isBlocked) {
          el.setAttribute("data-status", "blocked");
          el.setAttribute("title", "Unavailable");
          // console.log("Decorated BLOCKED (range) cell", { date: date.toDateString() });
          return; // stop processing this cell entirely
        }

        const match = enrichedWindows.find((w) => dayKey(date) >= dayKey(w.start) && dayKey(date) <= dayKey(w.end));

        if (isBlocked && (!match || match.status !== "full")) {
          console.log("⚠️ Early BLOCKED override", {
            date: date.toDateString(),
            isBlocked,
            matchExists: !!match,
            matchStatus: match?.status,
            className: match?.className,
            isPrivate: match?.isPrivate,
          });
          el.setAttribute("data-status", "blocked");
          el.setAttribute("title", "Unavailable");
          // console.log("Decorated BLOCKED (isBlocked+noFull) cell", {
          //   date: date.toDateString(),
          //   className: match?.className,
          // });
          return; // stop processing this cell
        }

        if (match) {
          const isAllClasses = !selectedClassName || selectedClassName === "All Classes";
          const isSelectedClass = match.className === selectedClassName;
          const hasStarted = normalizeDate(today) >= normalizeDate(match.start);
          const isFuture = normalizeDate(date) > normalizeDate(today);

          if ((isAllClasses || isSelectedClass) && isFuture && !hasStarted) {
            const type = match.isFixed ? "fixed" : "regular";

            if (match.status === "available") {
              const totalRooms = Object.keys(match.occupancy).length || 3;
              const remaining = match.remainingRooms;

              if (remaining === 0) {
                // full
                el.setAttribute("data-type", type);
                el.setAttribute("data-status", "full");
                el.setAttribute("title", `${match.className} — Fully booked`);
                // console.log("Decorated AVAILABLE→FULL cell", {
                //   date: date.toDateString(),
                //   className: match.className,
                //   type,
                //   status: "full",
                //   remaining,
                // });
                return;
              }

              // map remaining → shade
              let shade = 0;
              if (remaining === 2) shade = 1;
              if (remaining === 1) shade = 2;

              el.setAttribute("data-type", type);
              el.setAttribute("data-status", "available");
              el.setAttribute("data-shade", shade.toString());
              el.setAttribute("title", `${match.className} — ${pluralizeRooms(remaining)}`);
              // console.log("Decorated AVAILABLE cell", {
              //   date: date.toDateString(),
              //   className: match.className,
              //   type,
              //   status: "available",
              //   remaining,
              //   shade,
              // });
            } else if (match.status === "full") {
              el.setAttribute("data-type", type);
              el.setAttribute("data-status", "full");
              el.setAttribute("title", `${match.className} — Fully booked`);
              // console.log("Decorated FULL cell", {
              //   date: date.toDateString(),
              //   className: match.className,
              //   type,
              //   status: "full",
              //   isPrivate: match.isPrivate,
              // });
            } else {
              el.setAttribute("data-status", "blocked");
              el.setAttribute("title", "Unavailable");
              // console.log("Decorated BLOCKED (else) cell", {
              //   date: date.toDateString(),
              //   className: match.className,
              //   status: "blocked",
              // });
            }

            if (content) {
              const bg = window.getComputedStyle(content).backgroundColor;
              const textColor = getTextColor(rgbToHex(bg));
              content.style.color = textColor;
            }
          } else {
            el.setAttribute("data-status", "blocked");
            el.setAttribute("title", "Unavailable");
            // console.log("Decorated BLOCKED (not future/started) cell", {
            //   date: date.toDateString(),
            //   className: match.className,
            // });
          }
        }

        // Highlight selected range boundaries
        if (Array.isArray(watchedDates) && watchedDates.length === 2) {
          const [selStart, selEnd] = watchedDates.map((d) => {
            const nd = new Date(d);
            nd.setHours(0, 0, 0, 0);
            return nd.getTime();
          });

          const time = date.getTime();
          if (time === selStart) {
            el.setAttribute("data-range", "start");
            // console.log("Marked RANGE START", { date: date.toDateString() });
          } else if (time === selEnd) {
            el.setAttribute("data-range", "end");
            // console.log("Marked RANGE END", { date: date.toDateString() });
          }
        }
      });
    });
  }, [calendarMonth, calendarYear, enrichedWindows, watchedDates, selectedClassName, filteredBlockedDates]);

  useEffect(() => {
    const shortLabels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    requestAnimationFrame(() => {
      const allWeekEls = document.querySelectorAll(".calendar__week-content");

      // The last 7 are always the visible ones
      const visibleWeekEls = Array.from(allWeekEls).slice(-7);

      visibleWeekEls.forEach((el, i) => {
        el.textContent = shortLabels[i];
      });
    });
  }, [calendarMonth, isDesktop, calendarYear, calendarReady, formStep]);

  const matchedClass = fixedWindows?.find((f) => parentUrl.toLowerCase().includes(f.className.toLowerCase().replace(/\s+/g, "-")));

  // console.log("Matched fixed class:", matchedClass);

  const checkDateOverlap = (selRange, resDates) => {
    // console.log("Checking Overlaps");

    const [st1, et1] = selRange.map(normalizeDate);

    for (const { startDate, endDate } of resDates) {
      const st2 = normalizeDate(startDate);
      const et2 = normalizeDate(endDate);

      const overlap = (st1 >= st2 && st1 <= et2) || (et1 >= st2 && et1 <= et2) || (st2 >= st1 && st2 <= et1) || (et2 >= st1 && et2 <= et1);

      // console.log("Overlaps: ", overlap);
      if (overlap) return true;
    }

    return false;
  };

  function add_days(date, days) {
    const dt = new Date(date);
    dt.setDate(dt.getDate() + days);
    return dt;
  }

  // Calculate unused rooms based on guest count
  function getUnusedRoomCount(guestCount: number): number {
    const roomsUsed = Math.ceil(guestCount / 2);
    const totalRooms = 3;
    return Math.max(0, totalRooms - roomsUsed);
  }

  // Build the correct private/standard service label
  function getPrivateServiceLabel(duration: string, unusedRooms: number, isPrivate: boolean): string {
    if (!isPrivate || guestCount >= 3) {
      return "Standard"; // ✅ always resolve to the $0.00 option
    }

    const durCode = duration === "0" ? "3D" : "5D"; // "0" = 3-day, "1" = 5-day
    return `Private ${durCode}${unusedRooms}R`;
  }

  function getOccupancySuffix(occupancy: string, allowSingleGuest: boolean): string {
    if (occupancy === "1 Single Room") {
      return allowSingleGuest ? "1G1S" : "";
    }

    const map: Record<string, string> = {
      "1 Double Room": "2G1D",
      "2 Single Rooms": "2G2S",
      "1 Single Room / 1 Double Room": "3G1S1D",
      "3 Single Rooms": "3G3S",
      "2 Double Rooms": "4G2D",
      "2 Single Rooms / 1 Double Room": "4G2S1D",
      "1 Single Room / 2 Double Rooms": "5G1S2D",
      "3 Double Rooms": "6G3D",
    };

    return map[occupancy] || "";
  }

  function getDefaultServicePrefix(duration: string): string {
    if (duration === "0") return "3D";
    if (duration === "1") return "5D";
    return "";
  }

  function buildServiceLabel(classMeta: any, duration: string, occupancy: string, allowSingleGuest: boolean): string {
    const prefix = classMeta?.servicePrefix ?? getDefaultServicePrefix(duration);
    const suffix = getOccupancySuffix(occupancy, allowSingleGuest);

    if (!prefix || !suffix) return "";

    const separator = classMeta?.serviceSeparator ?? "";

    return `${prefix}${separator}${suffix}`;
  }

  function getClassTypeServiceId(exp: string, servs: any[]) {
    let label = "Stay";

    if (exp === "0") {
      label = "3-Day";
    } else if (exp === "1") {
      label = "5-Day";
    }

    return servs.find((s) => s.label === label)?.id;
  }

  useEffect(() => {
    // console.log("💰 Pricing effect inputs", { ng, occ, cls, stayOption });
    // console.log("ParentURL = ", parentUrl);

    const frmData: Record<string, any> = {};

    if (!srvQuery.isLoading && servs) {
      let optionCode = "";

      // --- Room mode override ---
      if (isRoomMode) {
        optionCode = "ROOM";
      } else {
        // --- occ/cls → optionCode mapping ---
        const currentClassMeta = CLASS_BY_NAME[getValues("ExperienceTitle")];

        const allowSingleGuest = isPrescheduledPage || !!selectedWindow || !!(currentClassMeta as any)?.servicePrefix;

        optionCode = buildServiceLabel(currentClassMeta, cls, occ, allowSingleGuest);
      }

      if (optionCode) {
        setStayOption(optionCode);
        frmData[num_guests_uuid] = optionCode;
      }

      // --- Base service records ---
      const effectiveStayOption = optionCode || stayOption;
      const priceRec = servs.find((f) => f.label === effectiveStayOption);
      const exp = getValues("Experience");

      let classRec;
      if (exp === "0") {
        classRec = servs.find((f) => f.label === "3-Day");
      } else if (exp === "1") {
        classRec = servs.find((f) => f.label === "5-Day");
      } else {
        // ✅ Room mode or other values → use "Stay"
        classRec = servs.find((f) => f.label === "Stay");
      }

      setStayCode(priceRec?.id);
      setClassDur(classRec?.id);

      // --- Private surcharge logic ---
      // const guestCount = parseInt(getValues("NumberofGuests") ?? "0", 10);
      // const unusedRooms = getUnusedRoomCount(guestCount);
      const isPrivate = !!getValues("PrivateExperience");

      // Build the label: "Standard" or "Private 3D2R" etc.
      const privateLabel = getPrivateServiceLabel(getValues("Experience"), 1, isPrivate);
      // console.log("Determined privateLabel:", privateLabel, { isPrivate });
      const privateRec = servs.find((s) => s.label === privateLabel);
      if (privateRec) {
        frmData[private_parent_uuid] = privateRec.id;
        setPrivateStayId(privateRec.id); // ✅ always persist a valid UUID
      }

      // --- Pricing ---
      let basePrice = 0;
      if (isRoomMode) {
        const nights = numNights || 0; // bubbled up from CalendarBlock
        basePrice = nights * (priceRec?.price || 0);
      } else {
        basePrice = priceRec?.price || 0;
      }
      const privatePrice = isPrivate && privateRec?.price ? privateRec.price : 0;
      const totalPrice = basePrice + privatePrice;

      if (totalPrice && taxQuery.data) {
        applyIncludedTaxes(totalPrice, taxQuery.data, setValue, Euro);
      } else {
        // console.warn("⚠️ Missing priceRec or tax data — using fallback price");
        const fallbackPrice = getValues("Experience") == "0" ? 670000 : 1005000;
        applyIncludedTaxes(fallbackPrice, taxQuery.data, setValue, Euro);
      }

      // --- Payment schedule ---
      if (getValues("dates")) {
        const svcIDs = [priceRec?.id];
        if (isPrivate && privateRec?.id) svcIDs.push(privateRec.id);

        // console.log("paySchedule sample:", paySchedule?.slice?.(0, 5));
        // console.log("priceRec.id:", priceRec?.id);
        // Use occupancy schedule as the driver
        const occSchedule = paySchedule?.filter((f) => f.svc === priceRec?.id);
        console.log("💳 Selected payment schedule", {
          effectiveStayOption,
          priceRecLabel: priceRec?.label,
          priceRecId: priceRec?.id,
          occSchedule,
        });
        if (!occSchedule || occSchedule.length === 0) {
          console.warn("No occupancy schedule found for selected service");
          return;
        }

        const dates = getValues("dates");
        const arrival = Array.isArray(dates) && dates.length > 0 ? new Date(dates[0]) : null;
        if (!arrival) return;

        const today = new Date();
        const todayBase = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const arrivalBase = new Date(arrival.getFullYear(), arrival.getMonth(), arrival.getDate());

        const EuroFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" });

        const rawPayments = occSchedule.map((entry) => {
          const offset = parseInt(entry.offset, 10);
          const pct = parseInt(entry.vl, 10);
          const base = entry.mmnt === "request" ? todayBase : arrivalBase;
          const due = add_days(new Date(base), offset);
          const dueClamped = due < todayBase ? todayBase : due;
          const amtEuros = (totalPrice * pct) / 10000;
          return { pct, amt: amtEuros, due: dueClamped };
        });

        // Merge payments with identical due dates
        const mergedPayments: { pct: number; amt: number; due: Date }[] = [];
        rawPayments.forEach((p) => {
          const existing = mergedPayments.find((m) => m.due.getTime() === p.due.getTime());
          if (existing) {
            existing.amt += p.amt;
            // keep pct from occupancy schedule (don’t sum)
          } else {
            mergedPayments.push({ ...p });
          }
        });

        mergedPayments.sort((a, b) => a.due.getTime() - b.due.getTime());

        const allPastDue = mergedPayments.every((p) => p.due <= todayBase);
        if (allPastDue) {
          const totalAmt = mergedPayments.reduce((sum, p) => sum + p.amt, 0);
          setnumPmts(1);
          setValue("pct1", "100"); // collapsed into one payment
          setValue("pmt1", EuroFmt.format(totalAmt));
          setValue("due1", todayBase.toDateString());
        } else {
          mergedPayments.forEach((p, idx) => {
            const i = idx + 1;
            setValue(`pct${i}`, String(p.pct));
            setValue(`pmt${i}`, EuroFmt.format(p.amt));
            setValue(`due${i}`, p.due.toDateString());
          });
          setnumPmts(mergedPayments.length);
        }
      }
    }
  }, [occ, cls, dts, ng, stayOption, servs, taxQuery.data, isPrivate]);

  const CONTACT_FIELDS = ["First name", "Last name", "Email", "phone", "Address", "City", "State", "PostalCode", "Country"];

  const getFieldsForCurrentStep = (): string[] => {
    if (isRoomMode) {
      if (isDesktop) {
        if (formStep === 0) return ["ExperienceTitle", "NumberofGuests", "dates"];
        if (formStep === 1) return CONTACT_FIELDS;
      } else {
        if (formStep === 0) return ["ExperienceTitle"];
        if (formStep === 1) return ["NumberofGuests", "dates"];
        if (formStep === 2) return CONTACT_FIELDS;
      }

      return [];
    }

    if (isDesktop) {
      if (formStep === 0) {
        return ["Experience", "ExperienceTitle", "NumberofGuests", "Occupancy", "dates"];
      }

      if (formStep === 1) return CONTACT_FIELDS;

      if (formStep === 2) return ["student_info", "allergies"];
    } else {
      if (formStep === 0) return ["Experience", "ExperienceTitle"];

      if (formStep === 1) {
        return ["NumberofGuests", "Occupancy", "dates"];
      }

      if (formStep === 2) return CONTACT_FIELDS;

      if (formStep === 3) return ["student_info", "allergies"];
    }

    return [];
  };

  const currentStepFields = getFieldsForCurrentStep();
  const currentStepValues = watch(currentStepFields as any);

  const isCurrentStepComplete = currentStepFields.every((field, index) => {
    const value = currentStepValues?.[index];

    if (field === "dates") {
      return Array.isArray(value) && value.length >= 2;
    }

    return value !== undefined && value !== null && String(value).trim() !== "";
  });

  const completeFormStep = async () => {
    const fields = getFieldsForCurrentStep();

    const stepIsValid = fields.length === 0 ? true : await trigger(fields as any, { shouldFocus: true });

    if (!stepIsValid) {
      return;
    }

    setStepsCompleted((prev) => {
      const updated = [...prev];
      updated[formStep] = true;
      return updated;
    });

    setFormStep((cur) => Math.min(cur + 1, stepsCompleted.length - 1));
  };

  const handleStepClick = (i: number) => {
    if (i <= formStep) {
      // always allow going back
      setFormStep(i);
    } else if (stepsCompleted[i - 1]) {
      // only allow forward if the previous step is complete
      setFormStep(i);
    }
  };

  useEffect(() => {
    const monthIndex = calendarMonth + 1;

    requestAnimationFrame(() => {
      const calendarEls = document.querySelectorAll(".calendar-root");

      calendarEls.forEach((el) => {
        el.setAttribute("data-current-month", String(monthIndex));
        el.setAttribute("data-current-year", String(calendarYear));
      });
    });
  }, [calendarMonth, calendarYear, isDesktop, calendarReady, formStep]);

  // 🔍 Debug counters

  const renderButton = () => {
    // No button after final step
    if (formStep > MAXSTEPS) {
      return null;
    }

    const isFinalStep = formStep === MAXSTEPS;

    // FINAL STEP → Checkout button
    if (isFinalStep) {
      buildPayload(); // Prepare payload before checkout

      if (checkingOut) {
        // 🔄 Show spinner while processing
        return (
          <button
            type="button"
            onClick={() => sendPayload(bookingData)}
            disabled={!isValid || checkingOut}
            className="bg-[#8c9c9b] text-white rounded px-2 py-2 flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <svg className="animate-spin -ml-1 mr-3 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Processing…
          </button>
        );
      }

      // Normal final checkout button
      return (
        <button
          type="button"
          onClick={() => sendPayload(bookingData)}
          disabled={!isValid}
          className="bg-[#8c9c9b] text-white rounded px-4 py-2 flex items-center disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Check Out
        </button>
      );
    }

    // ANY OTHER STEP → Continue button
    return (
      <button
        type="button"
        onClick={completeFormStep}
        disabled={!isCurrentStepComplete}
        className="mb-2 mt-2 bg-[#8c9c9b] text-white rounded px-8 py-2 flex items-center disabled:bg-gray-300 disabled:cursor-not-allowed"
      >
        Continue
      </button>
    );
  };
  var geocodeByPlaceId = function (placeId) {
    var geocoder = new window.google.maps.Geocoder();
    var OK = window.google.maps.GeocoderStatus.OK;

    return new Promise(function (resolve, reject) {
      geocoder.geocode({ placeId: placeId }, function (results, status) {
        if (status !== OK) {
          return reject(status);
        }
        return resolve(results);
      });
    });
  };

  var pid;
  var adr_comps;
  var adr;
  var post_cd;
  var city;
  var state_reg;
  var country;
  var resDates;

  function handleClassChange(newClassName: string) {
    const currentDates = getValues("dates");

    // Case 1: user already has a free/open range selected
    if (Array.isArray(currentDates) && currentDates.length === 2 && !selectedWindow) {
      const [selStart] = currentDates.map(normalizeDate);
      const defaultDuration = getValues("Experience") === "1" ? 6 : 4; // 5-day = 6 nights, 3-day = 4 nights
      const newEnd = normalizeDate(add_days(selStart, defaultDuration));

      setValue("dates", [selStart, newEnd], { shouldValidate: true, shouldDirty: true });
    }

    // Case 2: user has a scheduled window selected
    let isValidForNewClass = false;
    if (Array.isArray(currentDates) && currentDates.length === 2 && selectedWindow) {
      const [selStart, selEnd] = currentDates.map(normalizeDate);
      isValidForNewClass = availableWindows.some(
        (w) => w.className === newClassName && dayKey(w.start) === dayKey(selStart) && dayKey(w.end) === dayKey(selEnd),
      );
    }

    // If the current selection doesn’t match the new class → clear it
    if (selectedWindow && !isValidForNewClass) {
      setValue("dates", [], { shouldValidate: true, shouldDirty: true });
      setSelectedWindow(null);
    }

    // Always update the class name — but DO NOT touch Experience anymore
    setValue("ExperienceTitle", newClassName, { shouldValidate: true, shouldDirty: true });
  }

  const buildPayload = () => {
    const cal_dates = (getValues("dates") || []).map(normalizeDate);

    if (cal_dates.length < 2) {
      console.warn("⚠️ No valid dates selected.");
      alert("Please select your dates before continuing.");
      return;
    }

    bookingData.interval.start = toLocalYMD(cal_dates[0]);
    bookingData.interval.end = toLocalYMD(cal_dates[1]);

    const firstName = getValues("First name") || "";
    const lastName = getValues("Last name") || "";
    const email = getValues("Email") || "";
    const phone = getValues("phone") || getValues("Phone") || "";

    const addrData = {
      street: getValues("Address") || "",
      city: getValues("City") || "",
      state: getValues("State") || "",
      zip: getValues("PostalCode") || "",
      country: getValues("Country") || "",
    };

    // Old working service-ID path
    if (!stayCode || !classDur) {
      console.error("❌ Service state is not ready:", {
        stayOption,
        stayCode,
        classDur,
        experience: getValues("Experience"),
        occupancy: getValues("Occupancy"),
      });

      alert("Pricing information is not ready. Please refresh and try again.");
      return;
    }

    formData[num_guests_uuid] = stayCode;
    formData[class_type_uuid] = classDur;

    // Updated field shapes and current field names
    formData[name_uuid] = {
      first_name: firstName,
      last_name: lastName,
    };

    formData[email_uuid] = email;
    formData[phone_uuid] = phone;
    formData[addr_uuid] = addrData;

    formData[allergies_uuid] = parsedUrlInfo.isRoomMode ? "NA" : getValues("allergies") || "NA";

    formData[guest_details_uuid] = parsedUrlInfo.isRoomMode ? "NA" : getValues("student_info") || "NA";

    formData[class_name_uuid] = getValues("ExperienceTitle");

    if (privateStayId) {
      formData[private_parent_uuid] = privateStayId;
    }

    console.log("OLD-STYLE SERVICE STATE", {
      stayOption,
      stayCode,
      classDur,
      experience: getValues("Experience"),
      occupancy: getValues("Occupancy"),
    });

    // Continue with occupancy/product selection below

    console.log("🧾 Contact source values:", {
      firstName,
      lastName,
      email,
      phone,
      addrData,
    });

    console.log(
      "🧾 formData debug:",
      JSON.stringify(formData, (_key, value) => (value === undefined ? "__UNDEFINED__" : value), 2),
    );

    // 🧩 Occupancy assignment
    const guestCount = parseInt(getValues("NumberofGuests"), 10);
    const selectedClass = getValues("ExperienceTitle");

    // continue with the rest of your existing buildPayload code...

    // console.log("🔍 Using productCapacityMap data:", productCapacityMapQuery.data);

    // Match window by both class and dates
    let matchedWindow = enrichedWindows.find(
      (w) => w.className === selectedClass && dayKey(w.start) === dayKey(cal_dates[0]) && dayKey(w.end) === dayKey(cal_dates[1]),
    );

    if (!matchedWindow) {
      console.log("⚠️ No matching window found. Creating synthetic window for arbitrary stay.");

      matchedWindow = {
        className: selectedClass || "Booking",
        start: cal_dates[0],
        end: cal_dates[1],
        freeGroups: allKnownGroupIds,
        allGroups: allKnownGroupIds,
        remainingRooms: allKnownGroupIds.length,
        isAvailable: true,
        status: "available",
        isFixed: false,
      };
    }

    const freeGroups = matchedWindow?.freeGroups || [];
    const selectedSuiteName = getValues("ExperienceTitle");

    // 1. Find suite object based on selected name
    const selectedSuite = suiteProductIds.find((s) => s.productName === selectedSuiteName);

    // 2. User-selected suite wins
    let finalProductId = selectedSuite?.productId;

    // 3. Auto-select suite only if user didn't choose one
    if (!finalProductId && guestCount <= 2 && freeGroups.length > 0) {
      const matchSuite = suiteProductIds.find((suite) => {
        const pGroups = productGroupMap[suite.productId] || [];
        return pGroups.some((gid) => freeGroups.includes(gid));
      });

      if (matchSuite) {
        finalProductId = matchSuite.productId;
        console.log(`Auto-selected suite: ${matchSuite.productName}`);
      } else {
        finalProductId = eventProductId;
        console.log("⚠️ No suite matched. Using event unit.");
      }
    }
    // ⭐ NEW: If >2 guests in class mode → always use event product
    if (!parsedUrlInfo.isRoomMode && guestCount > 2) {
      finalProductId = eventProductId;
      console.log("Class mode: >2 guests → using event product.");
    }

    // 4. Assign final product_id
    bookingData.product_id = finalProductId;

    // 5. Use finalProductId for occupancy logic
    const productGroups = productGroupMap[finalProductId] || [];
    if (productGroups.length === 0) {
      console.warn("⚠️ No capacity groups available for product:", finalProductId);
      alert("No capacity groups available.");
      return;
    }

    const candidateGroups = productGroups.filter((gid) => freeGroups.includes(gid));
    if (candidateGroups.length === 0) {
      console.warn("⚠️ No free capacity groups available for product:", finalProductId);
      alert("No free capacity groups available.");
      return;
    }

    if (parsedUrlInfo.isRoomMode) {
      const groupId = candidateGroups[0]; // there's only one
      const guestCount = parseInt(getValues("NumberofGuests"), 10);

      const maxCap = groupCapacityMap[groupId]?.max ?? 0;
      if (guestCount > maxCap) {
        alert(`Suite cannot accommodate ${guestCount} guest(s). Max capacity is ${maxCap}.`);
        return;
      }

      bookingData.occupancy[groupId] = guestCount;

      console.log(`🛏️ Room mode: assigned ${guestCount} guest(s) to group ${groupId} (${groupNameMap[groupId]})`);
      bookingData.debug.capacities = { [groupId]: maxCap };
      bookingData.debug.productGroups = [groupId];
      bookingData.form_values = formData;
      bookingData.coupon_codes = couponState.code ? [couponState.code] : [];

      console.log("📦 Booking Payload Created (Room Mode):", JSON.stringify(bookingData, null, 2));

      return;
    } else {
      console.log("🧠 Selected product ID:", finalProductId);
      console.log("🔍 Product groups:", productGroupMap[finalProductId]);
      console.log("🔍 Free groups in window:", matchedWindow?.freeGroups);
      console.log("✅ Candidate groups for assignment:", candidateGroups);

      bookingData.occupancy = Object.fromEntries(productGroups.map((id) => [id, 0]));

      // 🧩 Parse room configuration from stayOption suffix
      function parseRoomConfig(stayOption: string) {
        if (!stayOption) return [];

        const guestsMatch = stayOption.match(/(\d+)G/);
        const totalGuests = guestsMatch ? parseInt(guestsMatch[1], 10) : parseInt(getValues("NumberofGuests") ?? "0", 10);

        const gIdx = stayOption.lastIndexOf("G");
        const suffix = gIdx >= 0 ? stayOption.slice(gIdx + 1) : "";

        const segments = suffix.match(/\d+(?:S|D)/g) || [];
        const roomAssignments = segments
          .map((seg) => {
            const count = parseInt(seg, 10);
            const perRoom = seg.endsWith("S") ? 1 : seg.endsWith("D") ? 2 : 1;
            return Array(count).fill(perRoom); // e.g. "2D" → [2,2]
          })
          .flat();

        return { roomAssignments, totalGuests };
      }

      const { roomAssignments, totalGuests } = parseRoomConfig(servs.find((service) => service.id === stayCode)?.label || stayOption);
      console.log("🧩 Parsed room assignments:", roomAssignments, "Total guests:", totalGuests);

      if (roomAssignments.length > candidateGroups.length) {
        alert("Not enough rooms available for the requested configuration.");
        return;
      }

      let guestsRemaining = totalGuests;
      for (let i = 0; i < roomAssignments.length; i++) {
        const groupId = candidateGroups[i];
        const assign = roomAssignments[i];
        const max = groupCapacityMap[groupId]?.max ?? 0;

        if (assign > max) {
          alert(`Room ${groupNameMap[groupId]} cannot accommodate ${assign} guest(s). Max capacity is ${max}.`);
          return;
        }

        bookingData.occupancy[groupId] = assign;
        guestsRemaining -= assign;
      }

      if (guestsRemaining > 0) {
        alert("Not enough total room capacity for the requested number of guests.");
        return;
      }
    }

    // Populate debug info for diagnostics
    bookingData.debug.capacities = Object.fromEntries(productGroups.map((id) => [id, groupCapacityMap[id]?.max || 0]));

    bookingData.debug.productGroups = productGroups;

    // 🧾 Log final occupancy map with readable names
    console.log("Final occupancy assignment:");
    Object.entries(bookingData.occupancy).forEach(([groupId, count]) => {
      const status = count > 0 ? "locked" : "free";
      console.log(`- ${groupNameMap[groupId]}: ${count} guest(s) → ${status}`);
    });

    if (couponState.code) {
      bookingData.coupon_codes = [couponState.code];
    } else {
      bookingData.coupon_codes = [];
    }

    bookingData.form_values = formData;
    // 📦 Log the full payload at creation time
    console.log("📦 Booking Payload Created:", JSON.stringify(bookingData, null, 2));
    console.log("SENDING", {
      start: bookingData.interval.start,
      end: bookingData.interval.end,
    });
  };

  let Euro = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
  });

  const fullDate = (d: Date) =>
    d.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  // Function to jump to a given date
  const jumpToDate = (date: Date | null) => {
    if (!date) return;
    const normalized = normalizeDate(date);
    setCalendarMonth(normalized.getMonth()); // e.g. 0 = January
    setCalendarYear(normalized.getFullYear()); // e.g. 2025
  };

  // useEffect(() => {
  //   // console.log("🔔 useEffect fired", { calendarMonth, calendarYear });

  //   const calendarEl = document.querySelector(".calendar-root");

  //   if (!calendarEl) {
  //     console.warn("⚠️ No element with class .calendar-root found in DOM");
  //     return;
  //   }

  //   if (calendarMonth === null || calendarYear === null) {
  //     console.warn("⚠️ Month/year state not set yet", {
  //       calendarMonth,
  //       calendarYear,
  //     });
  //     return;
  //   }

  //   const monthIndex = calendarMonth + 1; // JS months are 0–11
  //   calendarEl.setAttribute("data-current-month", String(monthIndex));
  //   calendarEl.setAttribute("data-current-year", String(calendarYear));

  //   // console.log("📅 Applied attributes to calendar-root:", {
  //   //   monthIndex,
  //   //   calendarYear,
  //   //   calendarEl,
  //   //   outerHTML: calendarEl.outerHTML, // dump the element markup
  //   // });

  //   // Optional: log all day cells to see what aria-labels exist
  //   const dayEls = calendarEl.querySelectorAll(".calendar__day-container");
  //   // console.log("📆 Found day cells:", dayEls.length);
  //   dayEls.forEach((el) => {
  //     // console.log(" - aria-label:", el.getAttribute("aria-label"));
  //   });
  // }, [calendarMonth, calendarYear]);

  // const getAddress = (goog_adr) => {
  //   if (goog_adr) {
  //     // console.log("Check it out", goog_adr);
  //     var adr = "";
  //     var pid = goog_adr.value.place_id;
  //     // console.log(pid);

  //     geocodeByPlaceId(pid).then(function (results) {
  //       adr_comps = results[0].address_components;

  //       for (var component of adr_comps) {
  //         var compType = component.types[0];

  //         switch (compType) {
  //           case "street_number": {
  //             adr = `${component.long_name} `;
  //             break;
  //           }
  //           case "route": {
  //             adr += component.long_name;
  //             // console.log(adr);
  //             setValue("Address", adr);
  //             break;
  //           }
  //           case "locality": {
  //             // console.log(component.long_name);
  //             setValue("City", component.long_name);
  //             break;
  //           }
  //           case "administrative_area_level_1": {
  //             // console.log(component.short_name);
  //             setValue("State", component.short_name);
  //             break;
  //           }
  //           case "postal_code": {
  //             // console.log(component.long_name);
  //             setValue("PostalCode", component.long_name);
  //             break;
  //           }
  //           case "country": {
  //             // console.log(component.long_name);
  //             setValue("Country", component.long_name);
  //             break;
  //           }
  //         }
  //       }
  //     });
  //   }
  // };

  // 🧠 Dynamic class name options
  const availableClassNames = useMemo(() => {
    // console.group("📚 availableClassNames computation");

    // console.log("Inputs:", {
    //   isPrescheduledPage,
    //   cls,
    //   enrichedWindowsCount: enrichedWindows?.length,
    //   allClassNames,
    //   classOptions,
    // });

    if (isSpecificFixedClassPage) {
      return parsedUrlInfo.className ? [parsedUrlInfo.className] : [];
    }

    if (isPrescheduledPage) {
      const all = enrichedWindows.filter((w) => w.isFixed && w.isAvailable);
      const filtered = cls === "" ? all : all.filter((w) => w.duration === cls);
      const names = [...new Set(filtered.map((w) => w.className).filter((n) => typeof n === "string" && n.trim() !== ""))];
      return names;
    }

    const result = cls === "" ? allClassNames : classOptions[cls] || [];
    // console.log("Non‑prescheduled result:", result);

    // console.groupEnd();
    return result;
  }, [enrichedWindows, cls, isPrescheduledPage, isSpecificFixedClassPage, parsedUrlInfo.className]);

  useEffect(() => {
    console.log("🧭 TIK fixed page debug:", {
      urlTail: parsedUrlInfo.urlTail,
      parsedClassName: parsedUrlInfo.className,
      parsedClassLen: parsedUrlInfo.classLen,
      isPrescheduledPage,
      isSpecificFixedClassPage,
      ttl,
      cls,
      availableClassNames,
    });
  }, [
    parsedUrlInfo.urlTail,
    parsedUrlInfo.className,
    parsedUrlInfo.classLen,
    isPrescheduledPage,
    isSpecificFixedClassPage,
    ttl,
    cls,
    availableClassNames,
  ]);

  if (dateQuery.isLoading || bookingsQuery.isLoading || productCapacityMapQuery.isLoading || bookingDetailsQuery.isLoading) {
    return <div>Loading…</div>;
  }

  if (dateQuery.isError || bookingsQuery.isError || productCapacityMapQuery.isError || bookingDetailsQuery.isError) {
    return <div>Error loading data.</div>;
  }

  return (
    <div className="flex w-full">
      {isDesktop ? (
        // DESKTOP LAYOUT — no responsive visibility classes here
        <div className="flex w-full">
          <div className="flex gap-8 w-full items-stretch" style={{ maxWidth: "1280px", width: "100%" }}>
            <aside className="flex justify-center rounded-lg shadow-2xl bg-white overflow-hidden" style={{ width: "var(--pane-left)" }}>
              <LeftPane
                classMeta={isRoomMode ? (selectedSuiteMeta ?? {}) : (selectedClassMeta ?? {})}
                classes={availableClasses}
                onBrowseClass={handleBrowseClass}
                mode={isRoomMode ? "suite" : isFixedMode ? "fixed" : "regular"}
                suiteImages={isRoomMode ? currentProductImages : undefined}
                pricing={resolvedPricing}
              />
            </aside>

            <main
              className="flex flex-col justify-between rounded-lg shadow-2xl bg-white px-6 overflow-hidden"
              style={{ width: "var(--pane-right)" }}
            >
              <ProgressIndicator
                stepLabels={stepLabels}
                formStep={formStep}
                handleStepClick={handleStepClick}
                stepsCompleted={stepsCompleted}
              />

              <div className="w-full">
                <form className="w-full">
                  {formStep < MAXSTEPS && formStep === 0 && (
                    <div className="flex flex-col gap-8 mb-4">
                      {/* 💾 Main Booking Section */}
                      <section className="flex flex-col gap-4">
                        {/* <h2 className="font-semibold text-2xl mb-0">Book Your Class Now</h2> */}

                        {/* 🧭 Class Selector — fixed/global pages only (top) */}
                        {isPrescheduledPage && !parsedUrlInfo.isRoomMode && (
                          <div id="class-selector">
                            {/* 📅 Currently Scheduled Classes */}
                            <label
                              id="classes-label"
                              htmlFor="class-selector"
                              className="text-left block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                            >
                              Currently Scheduled Classes
                            </label>
                            <ClassSelector
                              availableWindows={availableWindows}
                              selectedWindow={selectedWindow}
                              setSelectedWindow={(win) => {
                                setSelectedWindow(win);
                                const selectedRange = [normalizeDate(win.start), normalizeDate(win.end)];
                                setValue("dates", selectedRange, {
                                  shouldTouch: true,
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                                jumpToDate(win.start);
                              }}
                              setValue={setValue}
                              normalizeDate={normalizeDate}
                              jumpToDate={jumpToDate}
                              pluralizeRooms={pluralizeRooms}
                              isFixedPage={isPrescheduledPage}
                              isDesktop={isDesktop}
                            />
                          </div>
                        )}

                        {/* 🧣 Guest Details + 📅 Calendar */}
                        <div className="w-full max-w-[600px] mx-auto">
                          <div className="flex flex-col sm:flex-row items-start justify-center mx-auto sm:gap-x-6 gap-y-6">
                            {/* 🎓 Choose Your Class */}
                            <div className="flex-1 min-w-[245px]" style={{ width: "var(--guest-pane)" }}>
                              {/* Label OUTSIDE both frames */}
                              <label
                                id="guest-details-label"
                                htmlFor="guest-details"
                                className="text-left block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                              >
                                {isRoomMode ? "Choose Your Stay" : "Choose Your Class"}
                              </label>

                              {/* OUTER FRAME */}
                              <div
                                className="rounded-lg border"
                                style={{
                                  width: "var(--guest-box-width)",
                                  height: "var(--guest-pane-height)",
                                  padding: "var(--frame-outer-padding)",
                                  border: "var(--frame-outer-border)",
                                  borderRadius: "var(--frame-outer-radius)",
                                  background: "var(--frame-outer-bg)",
                                }}
                              >
                                {/* INNER FRAME */}
                                <div
                                  className="rounded-md border"
                                  style={{
                                    padding: "var(--frame-inner-padding)",
                                    border: "var(--frame-inner-border)",
                                    borderRadius: "var(--frame-inner-radius)",
                                    background: "var(--frame-inner-bg)",
                                    height: "100%",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  <GuestDetails
                                    register={register}
                                    setValue={setValue}
                                    watch={watch}
                                    availableClassNames={availableClassNames}
                                    guestOptions={guestOptions}
                                    occOptions={occOptions}
                                    guestCount={guestCount}
                                    unusedRooms={unusedRooms}
                                    onClassChange={handleClassChange}
                                    defaultClassName={parsedUrlInfo.className}
                                    isFixedMode={isFixedMode}
                                    isGlobalSearch={parsedUrlInfo.isGlobalSearch}
                                    selectedWindow={selectedWindow}
                                    availableDurations={availableDurations}
                                    onResetFilters={handleResetFilters}
                                    isRoomMode={parsedUrlInfo.isRoomMode}
                                    suiteImages={currentProductImages}
                                    classDurationMap={classDurationMap}
                                    isDesktop={isDesktop}
                                    isSpecificFixedClassPage={!!isSpecificFixedClassPage}
                                    durationLabels={durationLabels}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* RIGHT PANE */}
                            <div className="flex-1 min-w-[245px]" style={{ width: "var(--calendar-pane)" }}>
                              {/* Label OUTSIDE the frames */}
                              <label
                                id="calendar-label"
                                htmlFor="calendar-block"
                                className="text-left block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                              >
                                Select Dates
                              </label>

                              {/* OUTER BEIGE FRAME */}
                              <div
                                className="rounded-lg border"
                                style={{
                                  width: "var(--calendar-box-width)",
                                  height: "var(--calendar-pane-height)",
                                  padding: "var(--frame-outer-padding)",
                                  border: "var(--frame-outer-border)",
                                  borderRadius: "var(--frame-outer-radius)",
                                  background: "var(--frame-outer-bg)",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "var(--cal-stack-gap, 0.75rem)", // fallback if not defined
                                }}
                              >
                                {/* INNER FRAME #1 — CALENDAR */}
                                <div
                                  className="rounded-md border"
                                  style={{
                                    padding: "var(--frame-inner-padding)",
                                    border: "var(--frame-inner-border)",
                                    borderRadius: "var(--frame-inner-radius)",
                                    background: "var(--frame-inner-bg)",
                                  }}
                                >
                                  <CalendarBlock
                                    control={control}
                                    setValue={setValue}
                                    getValues={getValues}
                                    availableWindows={availableWindows}
                                    filteredBlockedDates={filteredBlockedDates}
                                    calendarMonth={calendarMonth}
                                    calendarYear={calendarYear}
                                    setCalendarMonth={setCalendarMonth}
                                    setCalendarYear={setCalendarYear}
                                    normalizeDate={normalizeDate}
                                    dayKey={dayKey}
                                    checkDateOverlap={checkDateOverlap}
                                    dts={dts}
                                    setSelectedWindow={setSelectedWindow}
                                    add_days={add_days}
                                    isRoomMode={parsedUrlInfo.isRoomMode}
                                    setNumNights={setNumNights}
                                    enrichedWindows={enrichedWindows}
                                    onCalendarMount={() => setCalendarReady(true)}
                                  />
                                </div>

                                {/* INNER FRAME #2 — LEGEND */}
                                <div
                                  className="rounded-md border"
                                  style={{
                                    padding: "var(--frame-inner-padding)",
                                    border: "var(--frame-inner-border)",
                                    borderRadius: "var(--frame-inner-radius)",
                                    background: "var(--frame-inner-bg)",
                                  }}
                                >
                                  {/* ORIGINAL LEGEND CARD — preserved exactly */}
                                  <div className="calendar-legend">
                                    <div className="legend-item">
                                      <span className="legend-swatch legend-open"></span>
                                      <span>Open Date</span>
                                    </div>

                                    <div className="legend-item">
                                      <span className="legend-swatch legend-fixed"></span>
                                      <span>Space Available</span>
                                    </div>

                                    <div className="legend-item">
                                      <span className="legend-swatch legend-full"></span>
                                      <span>Fully Booked</span>
                                    </div>

                                    <div className="legend-item">
                                      <span className="legend-swatch legend-blocked"></span>
                                      <span>Date Unavailable</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 🧭 Class Selector — class-specific pages only (bottom) */}
                        {!isPrescheduledPage && !parsedUrlInfo.isRoomMode && (
                          <div id="class-selector">
                            {/* 📅 Currently Scheduled Classes */}
                            <label
                              id="classes-label"
                              htmlFor="class-selector"
                              className="text-left block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                            >
                              Currently Scheduled Classes
                            </label>
                            <ClassSelector
                              availableWindows={availableWindows}
                              selectedWindow={selectedWindow}
                              setSelectedWindow={(win) => {
                                setSelectedWindow(win);
                                const selectedRange = [normalizeDate(win.start), normalizeDate(win.end)];
                                setValue("dates", selectedRange, {
                                  shouldTouch: true,
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                                jumpToDate(win.start);
                              }}
                              setValue={setValue}
                              normalizeDate={normalizeDate}
                              jumpToDate={jumpToDate}
                              pluralizeRooms={pluralizeRooms}
                              isFixedPage={isPrescheduledPage}
                              isDesktop={isDesktop}
                            />
                          </div>
                        )}
                      </section>
                    </div>
                  )}

                  {formStep === 1 && <FormStep1 register={register} errors={errors} control={control} setValue={setValue} />}

                  {!parsedUrlInfo.isRoomMode && formStep === 2 && <FormStep2 register={register} errors={errors} />}

                  {(parsedUrlInfo.isRoomMode ? formStep === 2 : formStep === 3) && (
                    <FormStep3
                      register={register}
                      getValues={getValues}
                      formStep={formStep}
                      parsedUrlInfo={parsedUrlInfo}
                      taxBreakdown={taxBreakdown}
                      numPmts={numPmts}
                      couponState={couponState}
                      isRoomMode={isRoomMode}
                      isDesktop={isDesktop}
                      fullDate={fullDate}
                    />
                  )}
                  {(parsedUrlInfo.isRoomMode ? formStep < 2 : formStep < 3) && (
                    <div id="pricing-summary">
                      {formStep !== 0 && (
                        <label
                          id="pricing-label"
                          htmlFor="pricing-summary"
                          className="text-left block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                        >
                          Reservation Summary
                        </label>
                      )}
                      <FormStepContext.Provider value={formStep}>
                        <PricingSummary
                          optionPrice={getValues("optionPrice")}
                          // VAT={getValues("VAT")}
                          taxBreakdown={taxBreakdown}
                          numPmts={numPmts}
                          register={register}
                          getValues={getValues}
                          hideBookingSummary={formStep === 0} // hide top line on step 0
                          hidePaymentSchedule={formStep === 0 || parsedUrlInfo.isRoomMode} // hide bottom line on step 0
                          couponState={couponState}
                          isRoomMode={parsedUrlInfo.isRoomMode}
                          isDesktop={isDesktop}
                        />
                      </FormStepContext.Provider>
                    </div>
                  )}
                </form>
              </div>

              <div className="flex flex-col w-full">
                <div className="py-2 border-t bg-white">
                  {(parsedUrlInfo.isRoomMode && formStep === 2) || (!parsedUrlInfo.isRoomMode && formStep === 3) ? (
                    <div className="flex items-center justify-between w-full">
                      {/* LEFT: Coupon UI */}
                      <div className="flex justify-start">
                        <CouponCheckout
                          register={register}
                          setValue={setValue}
                          handleApplyCoupon={handleApplyCoupon}
                          couponState={couponState}
                        />
                      </div>

                      {/* RIGHT: Checkout button */}
                      <div className="mt-3 flex items-center">{renderButton()}</div>
                    </div>
                  ) : (
                    <div className="flex justify-center w-full">{renderButton()}</div>
                  )}
                </div>

                <Footer />
              </div>
            </main>
          </div>
        </div>
      ) : (
        // MOBILE LAYOUT — only rendered when !isDesktop

        <MobileBookingLayout
          classMeta={isRoomMode ? (selectedSuiteMeta ?? {}) : (selectedClassMeta ?? {})}
          pricing={resolvedPricing}
          suiteImages={isRoomMode ? currentProductImages : undefined}
          isRoomMode={isRoomMode}
          progressBlock={
            <ProgressIndicator
              stepLabels={stepLabels}
              formStep={formStep}
              stepsCompleted={stepsCompleted}
              handleStepClick={handleStepClick}
            />
          }
          pageOne={
            formStep === 0 && (
              <LeftPaneMobile
                classMeta={isRoomMode ? (selectedSuiteMeta ?? {}) : (selectedClassMeta ?? {})}
                pricing={resolvedPricing}
                suiteImages={isRoomMode ? currentProductImages : undefined}
                isRoomMode={isRoomMode}
                watch={watch}
                setValue={setValue}
                availableClassNames={availableClassNames}
                availableDurations={availableDurations}
                onClassChange={handleClassChange}
                selectedWindow={selectedWindow}
                classDurationMap={classDurationMap}
                suiteProductIds={suiteProductIds}
                isSpecificFixedClassPage={!!isSpecificFixedClassPage}
                durationLabels={durationLabels}
              />
            )
          }
          guestDetailsBlock={
            formStep === 1 && (
              <div className="flex-1 min-w-[245px]" style={{ width: "var(--guest-pane)" }}>
                {/* Label OUTSIDE both frames */}
                <label
                  id="guest-details-label"
                  htmlFor="guest-details"
                  className="text-left block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                >
                  {isRoomMode ? "Choose Your Stay" : "Choose Your Class"}
                </label>

                {/* OUTER FRAME */}
                <div
                  className="rounded-lg border"
                  style={{
                    width: "var(--guest-box-width)",
                    height: "auto",
                    padding: "var(--frame-outer-padding)",
                    border: "var(--frame-outer-border)",
                    borderRadius: "var(--frame-outer-radius)",
                    background: "var(--frame-outer-bg)",
                  }}
                >
                  {/* INNER FRAME */}
                  <div
                    className="rounded-md border"
                    style={{
                      padding: "var(--frame-inner-padding)",
                      border: "var(--frame-inner-border)",
                      borderRadius: "var(--frame-inner-radius)",
                      background: "var(--frame-inner-bg)",
                      height: "100%",
                      overflowY: "auto",
                      overflowX: "hidden",
                      boxSizing: "border-box",
                    }}
                  >
                    <GuestDetails
                      register={register}
                      setValue={setValue}
                      watch={watch}
                      availableClassNames={availableClassNames}
                      guestOptions={guestOptions}
                      occOptions={occOptions}
                      guestCount={guestCount}
                      unusedRooms={unusedRooms}
                      onClassChange={handleClassChange}
                      defaultClassName={parsedUrlInfo.className}
                      isFixedMode={isFixedMode}
                      isGlobalSearch={parsedUrlInfo.isGlobalSearch}
                      selectedWindow={selectedWindow}
                      availableDurations={availableDurations}
                      onResetFilters={handleResetFilters}
                      isRoomMode={parsedUrlInfo.isRoomMode}
                      suiteImages={currentProductImages}
                      classDurationMap={classDurationMap}
                      isDesktop={isDesktop}
                      isSpecificFixedClassPage={!!isSpecificFixedClassPage}
                      durationLabels={durationLabels}
                    />
                  </div>
                </div>
              </div>
            )
          }
          classSelectorBlock={
            formStep === 1 &&
            !isRoomMode && (
              <React.Fragment key={isDesktop ? "desktop" : "mobile"}>
                <div className="w-full mb-3">
                  <label className="text-xs uppercase font-bold text-gray-700 mb-2 block">Currently Scheduled Classes</label>

                  <ClassSelector
                    availableWindows={availableWindows}
                    selectedWindow={selectedWindow}
                    setSelectedWindow={setSelectedWindow}
                    setValue={setValue}
                    normalizeDate={normalizeDate}
                    jumpToDate={jumpToDate}
                    pluralizeRooms={pluralizeRooms}
                    isFixedPage={isFixedMode}
                    isDesktop={isDesktop}
                  />
                </div>
              </React.Fragment>
            )
          }
          calendarBlock={
            formStep === 1 && (
              <div className="flex-1 min-w-[245px]" style={{ width: "var(--calendar-pane)" }}>
                {/* Label OUTSIDE the frames */}
                <label
                  id="calendar-label"
                  htmlFor="calendar-block"
                  className="text-left block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                >
                  Select Dates
                </label>

                {/* OUTER BEIGE FRAME */}
                <div
                  className="rounded-lg border"
                  style={{
                    width: "var(--calendar-box-width)",
                    height: "var(--calendar-pane-height)",
                    padding: "var(--frame-outer-padding)",
                    border: "var(--frame-outer-border)",
                    borderRadius: "var(--frame-outer-radius)",
                    background: "var(--frame-outer-bg)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--cal-stack-gap, 0.75rem)", // fallback if not defined
                  }}
                >
                  {/* INNER FRAME #1 — CALENDAR */}
                  <div
                    className="rounded-md border"
                    style={{
                      padding: "var(--frame-inner-padding)",
                      border: "var(--frame-inner-border)",
                      borderRadius: "var(--frame-inner-radius)",
                      background: "var(--frame-inner-bg)",
                    }}
                  >
                    <CalendarBlock
                      control={control}
                      setValue={setValue}
                      getValues={getValues}
                      availableWindows={availableWindows}
                      filteredBlockedDates={filteredBlockedDates}
                      calendarMonth={calendarMonth}
                      calendarYear={calendarYear}
                      setCalendarMonth={setCalendarMonth}
                      setCalendarYear={setCalendarYear}
                      normalizeDate={normalizeDate}
                      dayKey={dayKey}
                      checkDateOverlap={checkDateOverlap}
                      dts={dts}
                      setSelectedWindow={setSelectedWindow}
                      add_days={add_days}
                      isRoomMode={parsedUrlInfo.isRoomMode}
                      setNumNights={setNumNights}
                      enrichedWindows={enrichedWindows}
                      onCalendarMount={() => setCalendarReady(true)}
                    />
                  </div>

                  {/* INNER FRAME #2 — LEGEND */}
                </div>
              </div>
            )
          }
          pricingSummaryBlock={
            (parsedUrlInfo.isRoomMode ? formStep !== 0 && formStep < 3 : formStep !== 0 && formStep < 4) && (
              <div id="pricing-summary">
                {formStep > 1 && (
                  <label
                    id="pricing-label"
                    htmlFor="pricing-summary"
                    className="text-left block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  >
                    Reservation Summary
                  </label>
                )}

                <FormStepContext.Provider value={formStep}>
                  <PricingSummary
                    optionPrice={getValues("optionPrice")}
                    taxBreakdown={taxBreakdown}
                    numPmts={numPmts}
                    register={register}
                    getValues={getValues}
                    hideBookingSummary={formStep < 2}
                    hidePaymentSchedule={true}
                    couponState={couponState}
                    isRoomMode={parsedUrlInfo.isRoomMode}
                    isDesktop={isDesktop}
                  />
                </FormStepContext.Provider>
              </div>
            )
          }
          /*Coupon and Button block*/

          couponBlock={
            <div className="py-0 border-t bg-white">
              {(parsedUrlInfo.isRoomMode && formStep === 3) || (!parsedUrlInfo.isRoomMode && formStep === 4) ? (
                <div className="flex items-center justify-between w-full">
                  {/* LEFT: Coupon UI */}
                  <div className="flex justify-start">
                    <CouponCheckout
                      register={register}
                      setValue={setValue}
                      handleApplyCoupon={handleApplyCoupon}
                      couponState={couponState}
                    />
                  </div>

                  {/* RIGHT: Checkout button */}
                  <div className="mt- flex items-center mt-2">{renderButton()}</div>
                </div>
              ) : (
                <div className="flex justify-center w-full">{renderButton()}</div>
              )}
            </div>
          }
          /* Step content for steps 1–3 */
          stepContent={
            <>
              {formStep === 2 && <FormStep1 register={register} errors={errors} control={control} setValue={setValue} />}

              {formStep === 3 && !isRoomMode && <FormStep2 register={register} errors={errors} />}

              {(isRoomMode ? formStep === 3 : formStep === 4) && (
                <FormStep3
                  register={register}
                  getValues={getValues}
                  formStep={formStep}
                  parsedUrlInfo={parsedUrlInfo}
                  taxBreakdown={taxBreakdown}
                  numPmts={numPmts}
                  couponState={couponState}
                  isRoomMode={isRoomMode}
                  isDesktop={isDesktop}
                  fullDate={fullDate}
                />
              )}
            </>
          }
          // renderButton={renderButton}
        />
      )}
    </div>
  );
}
